import {
  DX,
  DY,
  EFFECT_ARMOR,
  EFFECT_CARRY,
  EFFECT_DOUBLE,
  EFFECT_FIRE,
  EFFECT_FROZEN,
  EFFECT_GHOST,
  EFFECT_MAGNET,
  EFFECT_SHIELD,
  EFFECT_SPEED,
  FOOD_ARMOR,
  FOOD_DOUBLE,
  FOOD_ELECTRIC,
  FOOD_FIRE,
  FOOD_FREEZE,
  FOOD_GHOST,
  FOOD_GOLDEN,
  isEdible,
  FOOD_MAGNET,
  FOOD_REMAINS,
  FOOD_REMAINS_BIG,
  FOOD_SPEED,
  FOOD_TELEPORT,
  FOOD_TROPHY,
  findSkin,
  REMAINS_LIFETIME_TICKS,
  type Rect,
  type Dir,
  type FoodKind,
  type MirrorSnake,
  type SkinDef,
  type SkinSignature,
  type StateMirror,
  type ThemeDef,
} from '@snakefeast/shared';
import { arenaBorder, arenaGradient, CHOMP_MS, drawCrown, drawSnake as drawSnakeArt, hex, isLight, mix, rgba, snakeDistance, snakeHeading, snakeIndexAt, snakePoint, snakeSamples, themeTile, type Pt, type SnakeShape } from './art';
import { artImage, artTint, FOODS, isArt, preloadArt } from './foodArt';

export interface RenderPlayer {
  slot: number;
  name: string;
  team: number;
  skin: SkinDef;
  isMe: boolean;
  /** The arena boss: drawn bigger. */
  boss?: boolean;
}

/** Your own snake online, drawn ahead of the others where it's predicted to be (see GameSession.ownView). */
export interface OwnSnake {
  snake: MirrorSnake;
  alpha: number;
  /** Cells its head has passed: food there isn't drawn. */
  reached: Set<number>;
}

export interface RenderOptions {
  theme: ThemeDef;
  quality: 'high' | 'low';
  reduceMotion: boolean;
  /** Minimum on-screen cell size in CSS px; larger arenas on phones get a follow camera. */
  minCellPx: number;
  /** Highest device pixel ratio drawn at in high quality (default 2). Phones use less: fill rate is their bottleneck. */
  maxDpr?: number;
  /** Darken the edges of the view in big arenas (default on). Phones skip it: it is a full-screen pass for a subtle effect. */
  vignette?: boolean;
  /** Draw player names above heads (off for the compact landing preview). */
  labels?: boolean;
  /** Vertical placement when the arena is shorter than the view ('top' leaves room below for touch controls). */
  align?: 'center' | 'top';
  /** Show every arena like a big one (zoomed follow camera, minimap), whatever its size. */
  bigView?: boolean;
  /** How closely the drawn head follows the real one (default smooth). */
  turnFeel?: TurnFeel;
  /** Cells across the shorter side of a big arena's view (default BIG_ARENA_VIEW_CELLS). Recorded clips show a little more. */
  viewCells?: number;
}

const TEAM_COLORS = ['#4da3ff', '#ff6b6b'];
/** Render slot for the solo ghost race: no player ever has it. */
const GHOST_SLOT = 255;
/** Death effect colours. */
const CONFETTI = ['#ff4d6d', '#ffd166', '#06d6a0', '#4cc9f0', '#c77dff', '#ff9e00'];
const FIREWORKS = ['#ffd166', '#ff4d6d', '#4cc9f0', '#c77dff', '#06d6a0'];
/** Signature skins' colors: the Diwali sparkler and crackers, dragon fire, Thunder's sparks, the Conqueror's stardust. */
const SPARKLER = ['#fff7dd', '#ffd166', '#ffb627'];
const CRACKERS = ['#ff3b3b', '#ffd166', '#2ee67b', '#ff5fa2'];
const FIRE = ['#ffd166', '#ff9f1c', '#ff4d1a'];
const STORM = ['#7fe7ff', '#ffffff'];
const STARDUST = ['#d8b4fe', '#ffd166', '#ffffff'];
/** Trail particles a boosting signature snake gives off per 60 Hz frame. */
const TRAIL_RATE: Record<SkinSignature, number> = { diwali: 2.6, dragon: 1.1, thunder: 1.3, royal: 0.8 };
/** How fast a swallowed meal moves down the body (cells a second), and how long eaten food takes to fly into the mouth (ms). */
const BULGE_CELLS_PER_S = 22;
/** However short the snake, a meal takes at least this long (ms) to go down it. */
const BULGE_MIN_MS = 450;
/**
 * Most meals going down one snake at a time. A long snake that keeps eating would otherwise carry
 * one for every meal of the last half minute (they take that long to reach its tail), all drawn
 * every frame; past this the oldest, far down the body, go.
 */
const BULGE_MAX = 8;
/**
 * Longer snakes show no meals going down: the swell is redrawn along the body every frame, and on a
 * snake this long it cost more frame time than anything else (the food still flies into its mouth).
 */
const BULGE_MAX_LENGTH = 150;
const GULP_MS = 160;
/** How far (cells) a snake's eyes look round for food. */
const LOOK_CELLS = 3;
/** Arenas wider than this are "big" (slither.io style): always a follow camera, drawn per frame. */
const BIG_ARENA = 70;
/** How much bigger the arena boss is drawn. */
const BOSS_SCALE = 1.35;
/**
 * How many cells fit across the shorter side of the screen in a big arena. 15 (was 20): the slim snakes
 * read bigger, with the ground between them kept, at the cost of seeing a little less of the arena.
 */
const BIG_ARENA_VIEW_CELLS = 15;
/** Largest cell in a big arena (CSS px), so a big monitor zooms in too instead of stopping early. */
const BIG_ARENA_MAX_CELL = 64;
/**
 * How far behind the real head the drawn one runs (cells), per turn feel. Every cell of lag is a
 * cell's worth of delay before a turn shows; too little, and a corner's curve isn't known yet when
 * the drawn head gets to it, so it nudges sideways onto the curve (a corner starts half a cell
 * before the cell's centre; at 0.6 cells of lag that nudge is under a hundredth of a cell, at 0.36
 * a few hundredths).
 *
 * - headLag: cells the drawn head trails the real one, so it can round each corner (see snakeShape).
 * - slack: a corner is shorter than a straight cell (a quarter circle: 0.79), so a head moving a cell
 *   a tick slowed down on every turn. The drawn head instead keeps its speed and runs up to this far
 *   (cells) further behind: it uses up the slack in a corner and builds it back slowly on the
 *   straight after (see drawnFront).
 * - maxLag: never further behind than this (cells): after a stall it catches up rather than trailing.
 * - lookAhead / lookBehind: cells ahead of and behind the head that its facing is averaged over (see
 *   snakeHeading), so a corner swings the head round over a stretch instead of in one cell. Ahead is
 *   at most headLag, as the path past the real head is unknown.
 */
const TURN_FEEL = {
  // Slack covers a U-turn's two corners (0.43 cells) and a zigzag's run of them: at 0.18 the drawn head
  // ran out of it and dropped to 78% speed for a few frames at every corner (measured at 60 fps), which
  // read as a hitch. Now its speed never jumps more than 2% from one frame to the next.
  smooth: { headLag: 0.36, slack: 0.45, maxLag: 0.75, lookAhead: 0.36, lookBehind: 0.7 },
  snappy: { headLag: 0.26, slack: 0.1, maxLag: 0.32, lookAhead: 0.24, lookBehind: 0.5 },
} as const;
export type TurnFeel = keyof typeof TURN_FEEL;
/** How hard the drawn head pulls back toward its slack behind (speed change per cell off it), and at most how much faster or slower. */
const CORNER_EASE = 0.35;
const CORNER_MAX_RATE = 0.2;
/** One frame at 60 Hz: the unit per-frame effects were tuned in. */
const FRAME_60_MS = 1000 / 60;
/** Seconds for the camera to catch up with the head. Time-based, so 120 Hz screens pan at the same speed as 60 Hz. */
const FOLLOW_EASE_S = 0.14;

/**
 * Critically damped spring toward `target` (Unity's SmoothDamp). Unlike plain easing its
 * speed never jumps, so the camera starts and stops gliding instead of jolting on a turn.
 * Returns the new value; `v` holds the velocity between calls.
 */
function smoothDamp(cur: number, target: number, v: { x: number }, smoothS: number, dt: number): number {
  const omega = 2 / smoothS;
  const x = omega * dt;
  const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  const change = cur - target;
  const temp = (v.x + omega * change) * dt;
  v.x = (v.x - omega * temp) * decay;
  return target + (change + temp) * decay;
}
/** Power-up pickups: color, and the drawn icon (or a text glyph) inside the disc. Shared with the HUD. */
export const POWERUP_STYLE: Record<number, { color: string; glyph: string; icon?: string }> = {
  [FOOD_GHOST]: { color: '#b4c6ff', glyph: '👻', icon: 'ghost' },
  [FOOD_DOUBLE]: { color: '#ffd166', glyph: '×2' },
  [FOOD_MAGNET]: { color: '#ff5fa2', glyph: '🧲', icon: 'magnet' },
  [FOOD_SPEED]: { color: '#7cff6b', glyph: '💨', icon: 'speed' },
  [FOOD_ARMOR]: { color: '#8fb3ff', glyph: '🛡️', icon: 'shield' },
  [FOOD_FREEZE]: { color: '#8fe9ff', glyph: '❄️', icon: 'snowflake' },
  [FOOD_TELEPORT]: { color: '#c38bff', glyph: '🌀', icon: 'spiral' },
  [FOOD_ELECTRIC]: { color: '#ffe94d', glyph: '⚡', icon: 'bolt' },
  [FOOD_FIRE]: { color: '#ff7a1a', glyph: '🔥', icon: 'flame' },
};
/** How long a shocked snake crackles with electricity. */
const SHOCK_MS = 1400;
/** Left-behind food pulses for this long before it disappears. */
const REMAINS_BLINK_MS = 3000;
const TROPHY_COLOR = '#ffd166';
/** Steps of the big-arena ground from the middle of the map to its edge (see drawArenaWindow). */
const GROUND_SHADES = 32;
/** Largest side of a cached ground block (device px, plus a tile); see groundBlock. */
const MAX_GROUND_BLOCK_PX = 1024;
const MAX_PARTICLES = 400;
/** Other snakes on screen drawn with their full skin (the nearest to you); see pickFancy. */
const FANCY_OTHERS = 3;
/** Particle shapes (pShape). */
const SHAPE_ROUND = 0;
const SHAPE_SQUARE = 1;
const SHAPE_STREAK = 2;
/** A streak is as long as this many frames of its motion. */
const STREAK_FRAMES = 3;
/**
 * Food sizes, as shares of a cell: each piece fits its own cell, so a pile of it reads as separate
 * pieces lying on the board, not stickers spilling over each other (at 1.45 and 2.4 a dead snake's
 * food covered the snakes near it). Left-behind food a little smaller, a long snake's a little bigger.
 */
const FOOD_SIZE = 1;
const REMAINS_SIZE = 0.9;
const BIG_REMAINS_SIZE = 1.15;
/** The soft glow behind a long snake's left-behind food, as a radius in cells: inside its own cell and a little round it. */
const BIG_REMAINS_GLOW = 0.75;
/**
 * The glow behind food on dark boards: how strong at the middle, and how far out (shares of the food's
 * size). Kept tight: every pixel of the sprite is blended, clear or not, and a phone pays for each one.
 */
const FOOD_GLOW_ALPHA = 0.55;
const FOOD_GLOW_REACH = 0.82;
/** A food sprite's side as a share of the emoji's size: room for its drop shadow, or for its glow. */
const SPRITE_PLAIN = 1.3;
const SPRITE_GLOW = FOOD_GLOW_REACH * 2 + 0.04;
/** Ambient specks drifting over the board (see drawMotes). */
const MOTES = 28;
/**
 * Emoji sprites redrawn per frame at a new sharpness (see emoji). Until its turn, a food shows its old
 * sprite scaled: redrawing every one at once (each with a blurred shadow) was a visible hitch.
 */
const SPRITE_REDRAWS_PER_FRAME = 3;
/** The radar (see drawRadar) points at golden stars and power-ups up to this far away (cells). */
const RADAR_CELLS = 40;
/** Food is drawn as fruit and treats; which one is picked from the cell, so it never flickers. */
const FRUITS = FOODS;
const fruitAt = (cell: number) => FRUITS[(Math.imul(cell, 2654435761) >>> 0) % FRUITS.length]!;
/**
 * The drawn art sits in a square of the sprite size, with a little margin, so it reads a bit smaller than
 * the emoji it replaced (those filled their box and looked bigger than a snake's head).
 */
const ART_BOX = 1.18;
/** Name prefixes the server gives bots and the boss, drawn as art before the name instead of as emoji. */
const NAME_ICON: Record<string, string> = { '🤖': 'robot', '👑': 'crown' };
const NAME_PREFIX = /^(\p{Extended_Pictographic})\uFE0F?\s+/u;
/** A name split into its drawn prefix icon (or null) and the rest. */
function nameParts(name: string): [string | null, string] {
  const m = NAME_PREFIX.exec(name);
  const icon = m ? NAME_ICON[m[1]!] : undefined;
  return icon ? [icon, name.slice(m![0].length)] : [null, name];
}
/** A pre-rendered sprite, and the pixel ratio it was drawn at. */
interface Sprite {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  dpr: number;
}
/** A cell's own phase (0..2π), so food on neighbouring cells doesn't pulse and glint in step. */
const phaseAt = (cell: number) => ((Math.imul(cell ^ 0x5bd1e995, 0x9e3779b1) >>> 0) / 4294967296) * Math.PI * 2;

/**
 * Canvas 2D renderer.
 * - Static arena (background, grid, border) is pre-rendered once to an offscreen canvas.
 * - Snakes are one stroked path each (cheap), interpolated between ticks for 60/120 fps motion.
 * - Particles live in a fixed-size typed-array pool: zero allocations per frame, no GC hitches.
 * - Device-pixel ratio is capped (2 on high, 1 on battery saver).
 */
export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private staticLayer: HTMLCanvasElement | OffscreenCanvas | null = null;
  private dpr = 1;
  private cell = 10;
  private viewW = 0;
  private viewH = 0;
  private camX = 0;
  private camY = 0;
  /** Spring velocity of the camera (px/s). */
  private camV = { x: { x: 0 }, y: { x: 0 } };
  private lastCamAt = 0;
  private cameraMode = false;
  private gridW = 0;
  private gridH = 0;
  private players = new Map<number, RenderPlayer>();
  // particle pool: x, y, vx, vy, life, maxLife
  private p = new Float32Array(MAX_PARTICLES * 6);
  private pColor: string[] = new Array(MAX_PARTICLES).fill('#fff');
  /** Each particle's size (CSS px): about a ninth of a cell for the usual sparks, bigger for death effects' confetti and shells. */
  private pSize = new Float32Array(MAX_PARTICLES).fill(3);
  /** SHAPE_ROUND (a soft spark), SHAPE_SQUARE (a confetti flake) or SHAPE_STREAK (a spark drawn along its motion). */
  private pShape = new Uint8Array(MAX_PARTICLES);
  private pCount = 0;
  private shake = 0;
  private get feel() {
    return TURN_FEEL[this.opts.turnFeel ?? 'smooth'];
  }
  private ghost: { snake: MirrorSnake; alpha: number; label: string; skin: SkinDef } | null = null;
  /** Full-screen color flash (kills, rank-ups): color and remaining strength 0..1. */
  private flashState: { color: string; k: number } | null = null;
  private zone = { inset: 0, warn: false, ring: 1 };
  /** King of the hill / capture the food overlays. */
  private objectives: { hill: Rect | null; hillMoving: boolean; bases: Rect[] } = { hill: null, hillMoving: false, bases: [] };
  /** Spectators: the slot the camera follows (null = the leader). */
  private follow: number | null = null;
  private nemesis: number | null = null;
  private bounty = false;
  private texts: Array<{ x: number; y: number; text: string; color: string; life: number; max: number; size: number }> = [];
  /** Emoji pre-rendered once per size (and the pixel ratio each was drawn at): drawImage is far cheaper than fillText every frame. */
  private sprites = new Map<string, Map<number, Sprite>>();
  /** Sprites this frame may still redraw at the current sharpness (see SPRITE_REDRAWS_PER_FRAME). */
  private spriteBudget = 0;
  private pattern: { cell: number; dpr: number; side: number; pats: Map<number, CanvasPattern | null> } | null = null;
  /** When each food item first appeared, for its pop-in animation. */
  private foodBorn = new Map<number, number>();
  /** Electric shock: slot -> time the crackle ends. */
  private shocked = new Map<number, number>();
  private magnetRadius = 1;
  private tickRate = 12;

  /** Glowing variants of the emoji sprites (see emoji), kept apart from the plain ones. */
  private glowSprites = new Map<string, Map<number, Sprite>>();

  /**
   * An emoji pre-rendered at one size, with its drop shadow; `glow` puts a soft halo of its own color behind it.
   * `force` redraws one made at another sharpness now, whatever is left of this frame's budget (drawing ahead in idle time).
   */
  private emoji(char: string, sizePx: number, glow = false, force = false): HTMLCanvasElement | OffscreenCanvas | null {
    const px = Math.max(8, Math.round(sizePx));
    // Drawn art decodes in a moment at start; until then there is nothing to draw (and nothing is cached).
    const art = isArt(char) ? artImage(char) : null;
    if (!art && isArt(char)) return null;
    // Looked up per food per frame: by character, then size (no key string to build each time).
    const cache = glow ? this.glowSprites : this.sprites;
    let sizes = cache.get(char);
    if (!sizes) {
      sizes = new Map();
      cache.set(char, sizes);
    }
    const hit = sizes.get(px);
    // One drawn at another sharpness (the quality just changed) is shown scaled until its turn to be redrawn.
    if (hit && (hit.dpr === this.dpr || (this.spriteBudget <= 0 && !force))) return hit.canvas;
    if (!force) this.spriteBudget--;
    const k = glow ? SPRITE_GLOW : SPRITE_PLAIN;
    const side = Math.ceil(px * k * this.dpr);
    const c: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(side, side) : Object.assign(document.createElement('canvas'), { width: side, height: side });
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.scale(this.dpr, this.dpr);
    const mid = (px * k) / 2;
    if (glow) {
      const tint = this.tintOf(char);
      const halo = g.createRadialGradient(mid, mid, 0, mid, mid, px * FOOD_GLOW_REACH);
      halo.addColorStop(0, rgba(tint, FOOD_GLOW_ALPHA));
      halo.addColorStop(0.45, rgba(tint, FOOD_GLOW_ALPHA * 0.45));
      halo.addColorStop(1, rgba(tint, 0));
      g.fillStyle = halo;
      g.fillRect(0, 0, px * k, px * k);
    }
    g.shadowColor = 'rgba(0,0,0,0.3)';
    g.shadowBlur = px * 0.1;
    g.shadowOffsetY = px * 0.06;
    if (art) {
      const box = px * ART_BOX;
      g.drawImage(art, mid - box / 2, mid - box / 2, box, box);
    } else {
      g.font = `${px}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(char, mid, mid + px * 0.04);
    }
    // Each emoji is drawn at a handful of sizes; resizing the window makes new ones, so keep it bounded.
    if (!hit && sizes.size >= 12) sizes.clear();
    sizes.set(px, { canvas: c, dpr: this.dpr });
    return c;
  }

  /**
   * `scale` grows or shrinks the sprite drawn at `sizePx` (pop-ins): the sprite stays the one
   * for the full size, as making one per in-between size rendered a dozen emoji (with a blurred
   * shadow) for every food that appeared, and a dying snake's remains made hundreds at once.
   */
  private drawEmoji(char: string, x: number, y: number, sizePx: number, scale = 1, glow = false) {
    const sprite = this.emoji(char, sizePx, glow);
    if (!sprite) return;
    const side = Math.max(8, Math.round(sizePx)) * (glow ? SPRITE_GLOW : SPRITE_PLAIN) * scale;
    this.ctx.drawImage(sprite as CanvasImageSource, x - side / 2, y - side / 2, side, side);
  }

  /** Each emoji's main color (see tintOf). */
  private tints = new Map<string, string>();

  /**
   * An emoji's main color as #rrggbb: its pixels averaged, the vivid ones counting most (an apple is
   * red, not the brown of its stem). Measured once per emoji on a tiny canvas, for its glow and sparks.
   */
  private tintOf(char: string): string {
    const art = artTint(char);
    if (art) return art;
    const hit = this.tints.get(char);
    if (hit) return hit;
    let tint = '#ffffff';
    try {
      const side = 24;
      const c: HTMLCanvasElement | OffscreenCanvas =
        typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(side, side) : Object.assign(document.createElement('canvas'), { width: side, height: side });
      const g = c.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
      g.font = `${side * 0.8}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(char, side / 2, side / 2);
      const d = g.getImageData(0, 0, side, side).data;
      let r = 0;
      let gr = 0;
      let b = 0;
      let w = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3]! < 128) continue;
        const hi = Math.max(d[i]!, d[i + 1]!, d[i + 2]!);
        const lo = Math.min(d[i]!, d[i + 1]!, d[i + 2]!);
        const sat = hi ? (hi - lo) / hi : 0;
        const k = (0.05 + sat * sat) * (hi / 255);
        r += d[i]! * k;
        gr += d[i + 1]! * k;
        b += d[i + 2]! * k;
        w += k;
      }
      if (w > 0) tint = hex(r / w, gr / w, b / w);
    } catch {
      /* canvas reads blocked (privacy settings): a white glow */
    }
    this.tints.set(char, tint);
    return tint;
  }

  /** The main color of the food drawn on a cell: its sparks fly in it when it's eaten. */
  foodColor(cell: number): string {
    return this.tintOf(fruitAt(cell));
  }

  /** What the food sprites were last drawn ahead for (size, sharpness, glow): see prewarmSprites. */
  private prewarmedFor = '';
  private prewarmingSprites = false;

  /**
   * Draw every fruit's sprite (food and left-behind food) ahead, while the browser is idle, a few at a
   * time: otherwise each kind is drawn (with a blurred shadow) the first time it comes into view, inside
   * a frame, and in a big arena that keeps happening all through the first minute.
   */
  private prewarmSprites() {
    const glow = this.opts.quality === 'high' && !isLight(this.opts.theme.background);
    const key = `${this.cell}|${this.dpr}|${glow}`;
    if (key === this.prewarmedFor || this.prewarmingSprites) return;
    this.prewarmedFor = key;
    this.prewarmingSprites = true;
    let i = 0;
    const next = (deadline?: IdleDeadline) => {
      // The size can change meanwhile: each sprite is drawn for the size of the moment.
      const cs = this.cell;
      const glowNow = this.opts.quality === 'high' && !isLight(this.opts.theme.background);
      do {
        const char = FRUITS[i % FRUITS.length]!;
        if (i < FRUITS.length) this.emoji(char, cs * FOOD_SIZE, glowNow, true);
        else this.emoji(char, cs * REMAINS_SIZE, false, true);
        i++;
      } while (i < FRUITS.length * 2 && deadline && deadline.timeRemaining() > 4);
      if (i < FRUITS.length * 2) schedule();
      else {
        this.prewarmingSprites = false;
        // If the size or sharpness changed while drawing, go again for the new one (a no-op otherwise).
        this.prewarmSprites();
      }
    };
    const schedule = () => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(next, { timeout: 500 });
      else setTimeout(next, 30);
    };
    schedule();
  }

  /** Soft round sparks, one sprite per color: a white-hot core in a halo of the color. */
  private sparks = new Map<string, HTMLCanvasElement | OffscreenCanvas>();

  private spark(color: string): HTMLCanvasElement | OffscreenCanvas {
    let c = this.sparks.get(color);
    if (c) return c;
    const side = 32;
    c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(side, side) : Object.assign(document.createElement('canvas'), { width: side, height: side });
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    const grad = g.createRadialGradient(side / 2, side / 2, 0, side / 2, side / 2, side / 2);
    const base = color.startsWith('#') ? color : '#ffffff';
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.22, rgba(base, 1));
    grad.addColorStop(0.5, rgba(base, 0.4));
    grad.addColorStop(1, rgba(base, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, side, side);
    // Colors are a handful of skins, power-ups and foods, but keep the cache bounded all the same.
    if (this.sparks.size > 96) this.sparks.clear();
    this.sparks.set(color, c);
    return c;
  }

  /** True when two cells are orthogonal neighbours (false across a wrap-around edge). */
  private adjacent(a: number, b: number): boolean {
    const dx = Math.abs((a % this.gridW) - (b % this.gridW));
    const dy = Math.abs(Math.floor(a / this.gridW) - Math.floor(b / this.gridW));
    return dx + dy === 1;
  }

  /** Floating combat-text style popup ("+5 ×3"), e.g. when eating. */
  emitText(cell: number, text: string, color: string, size = 1) {
    if (this.texts.length > 24) this.texts.shift();
    const max = this.opts.reduceMotion ? 30 : 48;
    this.texts.push({ x: this.cx(cell), y: this.cy(cell), text, color, life: 0, max, size });
  }

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private opts: RenderOptions,
  ) {
    // Not `desynchronized`: on Android Chrome that low-latency canvas can stop reaching the screen after the
    // fullscreen + landscape switch of big arenas. The game kept running (the minimap moved) but the arena froze.
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D unavailable');
    this.ctx = ctx;
    preloadArt();
  }

  /**
   * Change options (the auto quality governor steps sharpness and quality up and down mid-game).
   * Only what an option feeds is thrown away: a new theme repaints the ground; a new sharpness is worked
   * out at the next frame (applySize), and each cache notices it on its own.
   */
  setOptions(opts: Partial<RenderOptions>) {
    const theme = this.opts.theme;
    this.opts = { ...this.opts, ...opts };
    if (this.opts.theme !== theme) {
      this.staticLayer = null;
      this.pattern = null;
      this.blocks = [];
    }
    this.resize();
  }

  /** Battle royale: rings closed so far, whether the next step is about to close, and how many rings a step closes. */
  setZone(inset: number, warn: boolean, ring = 1) {
    this.zone.inset = inset;
    this.zone.warn = warn;
    this.zone.ring = ring;
  }

  setObjectives(hill: Rect | null, hillMoving: boolean, bases: Rect[]) {
    this.objectives = { hill, hillMoving, bases };
  }

  /** Match rules the drawing depends on (magnet reach, tick rate for food lifetimes). */
  setMatchRules(r: { magnetRadius: number; tickRate: number }) {
    this.magnetRadius = r.magnetRadius;
    this.tickRate = r.tickRate;
  }

  /** Electric shock: these snakes crackle for a moment. */
  shock(slots: number[], now: number) {
    for (const slot of slots) {
      this.shocked.set(slot, now + SHOCK_MS);
      if (this.players.get(slot)?.isMe && !this.opts.reduceMotion) this.shake = 12;
    }
  }

  setFollow(slot: number | null) {
    this.follow = slot;
  }

  /** Respawn arenas: the snake that last killed you (marked, with an arrow at the screen edge when it's off screen). */
  setNemesis(slot: number | null) {
    this.nemesis = slot;
  }

  /** Free-for-all respawn arenas: crown the leader, whose head carries a bounty. */
  setBounty(on: boolean) {
    this.bounty = on;
  }

  setPlayers(list: RenderPlayer[]) {
    this.players = new Map(list.map((p) => [p.slot, p]));
  }

  /**
   * Solo ghost race: a recorded run drawn translucent under your snake, with its name over the head.
   * Call after setPlayers; `snake` and `alpha` are updated every frame.
   */
  setGhost(g: { snake: MirrorSnake; alpha: number; label: string; skin: SkinDef } | null) {
    this.ghost = g;
    if (g) this.players.set(GHOST_SLOT, { slot: GHOST_SLOT, name: g.label, team: 0, skin: g.skin, isMe: false });
    else this.players.delete(GHOST_SLOT);
  }

  setGrid(width: number, height: number) {
    if (width === this.gridW && height === this.gridH) return;
    this.gridW = width;
    this.gridH = height;
    this.staticLayer = null;
    this.resize();
  }

  /** Big arenas never fit on screen: follow camera, minimap, per-frame background. */
  get isBigArena(): boolean {
    return !!this.opts.bigView || this.gridW > BIG_ARENA || this.gridH > BIG_ARENA;
  }

  /** The canvas's size, pixel ratio or cell may have changed: worked out again at the start of the next frame (see applySize). */
  private sizeDirty = true;

  /**
   * Note that the canvas may have changed size (or the options its scale depends on). Nothing is done
   * here: setting a canvas's size wipes it to black, and a ResizeObserver (or the quality governor,
   * after the frame is drawn) runs after this frame's drawing and just before the browser shows it,
   * so doing it here showed a black frame. The next frame does it right before drawing.
   */
  resize() {
    this.sizeDirty = true;
  }

  private applySize() {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !this.gridW) return;
    this.sizeDirty = false;
    const dpr = Math.min(window.devicePixelRatio || 1, this.opts.quality === 'high' ? (this.opts.maxDpr ?? 2) : 1);
    const width = Math.round(rect.width * dpr);
    const height = Math.round(rect.height * dpr);
    // Setting either one clears the canvas and makes a new one, even to the same number.
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    this.viewW = rect.width;
    this.viewH = rect.height;
    // Whole pixels in big arenas and quarter pixels on boards: a HUD bar a few pixels taller then
    // leaves the cell as it was, instead of every texture and sprite built for it being made again.
    let cell: number;
    if (this.isBigArena) {
      this.cameraMode = true;
      cell = Math.max(12, Math.min(BIG_ARENA_MAX_CELL, Math.round(Math.min(rect.width, rect.height) / (this.opts.viewCells ?? BIG_ARENA_VIEW_CELLS))));
    } else {
      const fit = Math.floor(Math.min(rect.width / this.gridW, rect.height / this.gridH) * 4) / 4;
      this.cameraMode = fit < this.opts.minCellPx;
      cell = this.cameraMode ? this.opts.minCellPx : fit;
    }
    if (cell !== this.cell || dpr !== this.dpr) this.staticLayer = null;
    this.cell = cell;
    this.dpr = dpr;
    this.prewarmSprites();
  }

  /** Visible part of the arena in cells (for the minimap). */
  viewRect(): { x: number; y: number; w: number; h: number } {
    return { x: -this.camX / this.cell, y: -this.camY / this.cell, w: this.viewW / this.cell, h: this.viewH / this.cell };
  }

  /**
   * The ground at one shade of the arena gradient: an opaque tile of that colour with the theme
   * texture on it, as a pattern in arena space (one tile = THEME_TILE / GRID_SPLIT cells).
   * Shades are cached: a few dozen small tiles cover the whole gradient.
   */
  private groundPattern(shade: number): CanvasPattern | null {
    if (this.pattern?.cell !== this.cell || this.pattern.dpr !== this.dpr) this.pattern = { cell: this.cell, dpr: this.dpr, side: 0, pats: new Map() };
    const hit = this.pattern.pats.get(shade);
    if (hit !== undefined) return hit;
    const { theme } = this.opts;
    // Bolder texture: with the camera following your snake, the ground sliding past is what shows you're moving.
    const texture = themeTile(theme, this.cell, this.dpr, true);
    const side = texture.width;
    this.pattern.side = side;
    const tile: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(side, side) : Object.assign(document.createElement('canvas'), { width: side, height: side });
    const g = tile.getContext('2d') as CanvasRenderingContext2D;
    g.fillStyle = mix(theme.background, theme.background2 ?? theme.background, shade / GROUND_SHADES);
    g.fillRect(0, 0, side, side);
    g.drawImage(texture as CanvasImageSource, 0, 0);
    const pat = this.ctx.createPattern(tile as CanvasImageSource, 'repeat');
    pat?.setTransform(new DOMMatrix().scale(1 / this.dpr));
    this.pattern.pats.set(shade, pat);
    return pat;
  }

  /** Recently used ground blocks (see groundBlock), newest last. */
  private blocks: Array<{ shade: number; cell: number; dpr: number; w: number; h: number; side: number; canvas: HTMLCanvasElement | OffscreenCanvas }> = [];

  /**
   * The ground at one shade, already tiled over a block a tile bigger than the view. Drawing it
   * is one 1:1 copy of opaque pixels, where filling the view with the pattern every frame made
   * a phone look up the texture for every pixel (as costly as all the snakes together).
   */
  private groundBlock(shade: number) {
    const hit = this.findBlock(shade);
    if (hit) {
      // Most recently used goes last, so the one dropped when full is the stalest.
      if (this.blocks[this.blocks.length - 1] !== hit) {
        this.blocks.splice(this.blocks.indexOf(hit), 1);
        this.blocks.push(hit);
      }
      this.prewarm(shade);
      return hit;
    }
    return this.buildBlock(shade);
  }

  /** A ground block at this shade, cell and sharpness. Any size will do (drawArenaWindow tiles it), so resizing the view keeps them. */
  private findBlock(shade: number) {
    for (const b of this.blocks) if (b.shade === shade && b.cell === this.cell && b.dpr === this.dpr) return b;
    return undefined;
  }

  private prewarmPending = false;

  /**
   * Build the shades either side of this one while the browser is idle, so moving across the arena
   * finds the next ground block ready instead of building a screen-sized canvas inside a frame (a
   * visible hitch on a phone, every time the shade changed).
   */
  private prewarm(shade: number) {
    if (this.prewarmPending) return;
    const next = [shade + 1, shade - 1].find((s) => s >= 0 && s <= GROUND_SHADES && !this.findBlock(s));
    if (next === undefined) return;
    this.prewarmPending = true;
    const run = () => {
      this.prewarmPending = false;
      if (!this.findBlock(next)) this.buildBlock(next);
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 50);
  }

  private buildBlock(shade: number) {
    const pat = this.groundPattern(shade);
    if (!pat) return null;
    // A tile's side in device pixels (whole, so blocks line up with the pattern exactly). Big
    // screens get a smaller block, drawn a few times over: a whole 4K view would be 60 MB a block.
    const side = this.pattern!.side;
    const cw = (Math.ceil(Math.min(this.viewW * this.dpr, MAX_GROUND_BLOCK_PX) / side) + 1) * side;
    const ch = (Math.ceil(Math.min(this.viewH * this.dpr, MAX_GROUND_BLOCK_PX) / side) + 1) * side;
    const canvas: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(cw, ch) : Object.assign(document.createElement('canvas'), { width: cw, height: ch });
    const g = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
    g.scale(this.dpr, this.dpr);
    g.fillStyle = pat;
    g.fillRect(0, 0, cw / this.dpr, ch / this.dpr);
    const block = { shade, cell: this.cell, dpr: this.dpr, w: this.viewW, h: this.viewH, side, canvas };
    // Four are kept: the one in view, the one it often sits on the line with, and the next shade
    // either way (built ahead in idle time). Each is about a screen's worth of pixels.
    this.blocks.push(block);
    if (this.blocks.length > 4) this.blocks.shift();
    return block;
  }

  /**
   * Background, texture and border for just the visible window (the full layer would be thousands of px).
   *
   * The arena's radial gradient spans the whole map while the view shows a small part of it, so
   * the ground is drawn in the shade the gradient has at the middle of the view: one copy of a
   * cached opaque block instead of a gradient fill plus a texture on top (two full-screen passes,
   * a third of a phone's frame).
   */
  private drawArenaWindow(ox: number, oy: number) {
    const ctx = this.ctx;
    const cs = this.cell;
    const { theme } = this.opts;
    const W = this.gridW * cs;
    const H = this.gridH * cs;
    const x0 = Math.max(0, -ox);
    const y0 = Math.max(0, -oy);
    const x1 = Math.min(W, this.viewW - ox);
    const y1 = Math.min(H, this.viewH - oy);
    // Same geometry as arenaGradient: from 0.1 of the short side to 0.75 of the long one.
    const r0 = Math.min(W, H) * 0.1;
    const r1 = Math.max(W, H) * 0.75;
    const d = Math.hypot(this.viewW / 2 - ox - W / 2, this.viewH / 2 - oy - H / 2);
    const shade = Math.round(Math.max(0, Math.min(1, (d - r0) / (r1 - r0))) * GROUND_SHADES);
    const block = x1 > x0 && y1 > y0 ? this.groundBlock(shade) : null;
    if (block) {
      // Each piece starts on a tile boundary at or before its corner; the camera is on whole device
      // pixels, so these copy pixels 1:1. Phones need one piece; a big screen a few.
      // Worked out in whole device pixels: at a ratio like 1.25 arena px land a hair off them, and
      // that alone made the browser resample the copy (three times slower).
      const k = this.dpr;
      const side = block.side;
      const bw = block.canvas.width;
      const bh = block.canvas.height;
      const ax = Math.round(ox * k);
      const ay = Math.round(oy * k);
      const X0 = Math.round(x0 * k);
      const Y0 = Math.round(y0 * k);
      const X1 = Math.round(x1 * k);
      const Y1 = Math.round(y1 * k);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.imageSmoothingEnabled = false;
      for (let y = Y0; y < Y1; ) {
        const sy = ((y % side) + side) % side;
        const h = Math.min(Y1 - y, bh - sy);
        for (let x = X0; x < X1; ) {
          const sx = ((x % side) + side) % side;
          const w = Math.min(X1 - x, bw - sx);
          ctx.drawImage(block.canvas as CanvasImageSource, sx, sy, w, h, x + ax, y + ay, w, h);
          x += w;
        }
        y += h;
      }
      ctx.restore();
    } else if (x1 > x0 && y1 > y0) {
      ctx.fillStyle = theme.background;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
    }
    arenaBorder(ctx, theme, W, H, cs, true);
    // Screen-space vignette: the edges of the view fall off a little, which gives depth.
    if (this.opts.quality === 'high' && this.opts.vignette !== false) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.drawImage(this.vignetteLayer() as CanvasImageSource, 0, 0, this.viewW, this.viewH);
      ctx.restore();
    }
  }

  private vignette: { w: number; h: number; layer: HTMLCanvasElement | OffscreenCanvas } | null = null;

  /**
   * The vignette, drawn once at a quarter of the view's size and stretched over it. A soft
   * gradient loses nothing scaled up, and a radial gradient filled over the whole screen every
   * frame cost a phone more than all the food.
   */
  private vignetteLayer(): HTMLCanvasElement | OffscreenCanvas {
    if (this.vignette?.w === this.viewW && this.vignette.h === this.viewH) return this.vignette.layer;
    const w = Math.max(1, Math.ceil(this.viewW / 4));
    const h = Math.max(1, Math.ceil(this.viewH / 4));
    const layer: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const g = layer.getContext('2d') as CanvasRenderingContext2D;
    const grad = g.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.75);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.35)');
    g.fillStyle = grad;
    g.fillRect(0, 0, w, h);
    this.vignette = { w: this.viewW, h: this.viewH, layer };
    return layer;
  }

  private buildStatic() {
    const { theme } = this.opts;
    const w = Math.ceil(this.gridW * this.cell * this.dpr);
    const h = Math.ceil(this.gridH * this.cell * this.dpr);
    const layer: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const c = layer.getContext('2d') as CanvasRenderingContext2D;
    c.scale(this.dpr, this.dpr);
    const cs = this.cell;
    const W = this.gridW * cs;
    const H = this.gridH * cs;
    c.fillStyle = arenaGradient(c, theme, W, H);
    c.fillRect(0, 0, W, H);
    const tile = themeTile(theme, cs, this.dpr);
    const pat = c.createPattern(tile as CanvasImageSource, 'repeat');
    if (pat) {
      pat.setTransform(new DOMMatrix().scale(1 / this.dpr));
      c.fillStyle = pat;
      c.fillRect(0, 0, W, H);
    }
    const g = c.createRadialGradient(W / 2, H / 2, cs * 4, W / 2, H / 2, W * 0.75);
    g.addColorStop(0, 'rgba(255,255,255,0.03)');
    g.addColorStop(1, 'rgba(0,0,0,0.22)');
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);
    arenaBorder(c, theme, W, H, cs);
    this.staticLayer = layer;
  }

  private cx(cell: number) {
    return ((cell % this.gridW) + 0.5) * this.cell;
  }
  private cy(cell: number) {
    return (Math.floor(cell / this.gridW) + 0.5) * this.cell;
  }

  /** Sparks flying out of a cell in every direction. `square` makes them flat flakes (confetti) instead of soft round sparks. */
  emitBurst(cell: number, color: string, count: number, speed = 1, size = Math.max(3, this.cell * 0.11), life = 1, square = false) {
    this.emitAt(this.cx(cell), this.cy(cell), color, count, speed, size, life, square);
  }

  /** A ring popping out of a cell: your own snake's bite. */
  emitPop(cell: number, color: string) {
    if (this.opts.reduceMotion) return;
    this.fx.push({ kind: 'ring', x: this.cx(cell), y: this.cy(cell), t0: this.lastRenderAt, dur: 300, color, size: this.cell * 1.35 });
  }

  private emitAt(x: number, y: number, color: string, count: number, speed: number, size: number, life: number, square: boolean) {
    if (this.opts.reduceMotion) return;
    const n = this.opts.quality === 'low' ? Math.ceil(count / 2) : count;
    for (let i = 0; i < n; i++) {
      const idx = this.pCount < MAX_PARTICLES ? this.pCount++ : Math.floor(Math.random() * MAX_PARTICLES);
      const a = Math.random() * Math.PI * 2;
      const v = (0.4 + Math.random()) * this.cell * 0.12 * speed;
      const o = idx * 6;
      this.p[o] = x;
      this.p[o + 1] = y;
      this.p[o + 2] = Math.cos(a) * v;
      this.p[o + 3] = Math.sin(a) * v;
      this.p[o + 4] = 0;
      this.p[o + 5] = (22 + Math.random() * 20) * life;
      this.pColor[idx] = color;
      this.pSize[idx] = size;
      this.pShape[idx] = square ? SHAPE_SQUARE : SHAPE_ROUND;
    }
  }

  /** One particle with its own velocity (px per 60 Hz frame) and life (frames): trails that stream a certain way. */
  private emitOne(x: number, y: number, vx: number, vy: number, color: string, size: number, life: number, shape: number) {
    const idx = this.pCount < MAX_PARTICLES ? this.pCount++ : Math.floor(Math.random() * MAX_PARTICLES);
    const o = idx * 6;
    this.p[o] = x;
    this.p[o + 1] = y;
    this.p[o + 2] = vx;
    this.p[o + 3] = vy;
    this.p[o + 4] = 0;
    this.p[o + 5] = life;
    this.pColor[idx] = color;
    this.pSize[idx] = size;
    this.pShape[idx] = shape;
  }

  /**
   * A boosting signature snake's trail, off its tail: the same number of particles a second at any frame
   * rate (half at low quality), all from the fixed pool, so a crowd of boosters can't run away with it.
   */
  private emitTrail(sig: SkinSignature, x: number, y: number) {
    const cs = this.cell;
    const rate = TRAIL_RATE[sig] * this.steps * (this.opts.quality === 'low' ? 0.5 : 1);
    let n = Math.floor(rate) + (Math.random() < rate % 1 ? 1 : 0);
    // The sparkler's white-hot tip: a glow that sits on the tail while it fizzes.
    if (sig === 'diwali' && n) this.emitOne(x, y, 0, 0, '#fff7dd', Math.max(3, cs * 0.2), 5, SHAPE_ROUND);
    while (n-- > 0) {
      const a = Math.random() * Math.PI * 2;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      if (sig === 'diwali') {
        // Sparkler: fast, short-lived streaks of white and gold.
        const v = cs * (0.12 + Math.random() * 0.16);
        this.emitOne(x, y, cos * v, sin * v, SPARKLER[n % SPARKLER.length]!, Math.max(2, cs * 0.1), 10 + Math.random() * 10, SHAPE_STREAK);
      } else if (sig === 'dragon') {
        // Fire: big soft flames drifting up as they fade.
        const v = cs * (0.01 + Math.random() * 0.03);
        const sx = x + (Math.random() - 0.5) * cs * 0.4;
        this.emitOne(sx, y, cos * v, sin * v - cs * 0.05, FIRE[n % FIRE.length]!, Math.max(3, cs * (0.16 + Math.random() * 0.12)), 22 + Math.random() * 14, SHAPE_ROUND);
      } else if (sig === 'thunder') {
        const v = cs * (0.08 + Math.random() * 0.1);
        this.emitOne(x, y, cos * v, sin * v, STORM[n % STORM.length]!, Math.max(1.5, cs * 0.09), 8 + Math.random() * 8, SHAPE_STREAK);
      } else {
        // Stardust: slow glittering flakes.
        const v = cs * (0.01 + Math.random() * 0.03);
        this.emitOne(x, y, cos * v, sin * v, STARDUST[n % STARDUST.length]!, Math.max(2, cs * 0.11), 30 + Math.random() * 20, n % 2 ? SHAPE_SQUARE : SHAPE_ROUND);
      }
    }
    // Now and then a sparkler crackles, and a storm throws a little bolt.
    if (sig === 'diwali' && Math.random() < 0.08 * this.steps) {
      for (let k = 0; k < 4; k++) {
        const a = Math.random() * Math.PI * 2;
        const v = cs * 0.06;
        this.emitOne(x + Math.cos(a) * cs * 0.6, y + Math.sin(a) * cs * 0.6, Math.cos(a) * v, Math.sin(a) * v, '#fff7dd', Math.max(1.5, cs * 0.07), 8, SHAPE_SQUARE);
      }
    } else if (sig === 'thunder' && Math.random() < 0.06 * this.steps) {
      const a = Math.random() * Math.PI * 2;
      const pts: Pt[] = [[x, y]];
      for (let k = 1; k <= 3; k++) pts.push([x + Math.cos(a) * cs * 0.5 * k + (Math.random() - 0.5) * cs * 0.4, y + Math.sin(a) * cs * 0.5 * k + (Math.random() - 0.5) * cs * 0.4]);
      this.fx.push({ kind: 'bolt', x, y, t0: this.lastRenderAt, dur: 140, color: '#7fe7ff', size: cs * 0.5, pts });
    }
  }

  /**
   * A signature skin's kill: played over the snake it just ate, on top of that snake's own death effect.
   * Like death effects, only a kill sets one off.
   */
  emitKill(body: number[], sig: SkinSignature) {
    if (this.opts.reduceMotion || !body.length) return;
    const head = body[0]!;
    const x = this.cx(head);
    const y = this.cy(head);
    const cs = this.cell;
    const t0 = this.lastRenderAt;
    switch (sig) {
      case 'diwali': {
        // A string of firecrackers: pops race down the body from the head, then one big bang on the head.
        const pops = Math.min(10, body.length);
        for (let k = 0; k < pops; k++) {
          const cell = body[Math.floor((k / pops) * body.length)]!;
          this.fx.push({ kind: 'pop', x: this.cx(cell), y: this.cy(cell), t0: t0 + k * 70, dur: 0, color: CRACKERS[k % CRACKERS.length]!, size: cs, cell });
        }
        this.fx.push({ kind: 'burst', x, y, t0: t0 + pops * 70 + 60, dur: 0, color: '#ffd166', size: cs, cell: head });
        break;
      }
      case 'dragon': {
        // Dragon fire: rings of flame and a blaze rising off the head, embers all down the body.
        this.fx.push({ kind: 'ring', x, y, t0, dur: 460, color: '#ff9f1c', size: cs * 3.4 });
        this.fx.push({ kind: 'ring', x, y, t0: t0 + 90, dur: 380, color: '#ffd166', size: cs * 2.2 });
        for (let i = 0; i < 26; i++) {
          const a = Math.random() * Math.PI * 2;
          const v = cs * (0.02 + Math.random() * 0.07);
          this.emitOne(x + (Math.random() - 0.5) * cs, y + (Math.random() - 0.5) * cs, Math.cos(a) * v, Math.sin(a) * v - cs * 0.07, FIRE[i % FIRE.length]!, Math.max(3, cs * (0.2 + Math.random() * 0.18)), 26 + Math.random() * 18, SHAPE_ROUND);
        }
        for (let i = 0; i < body.length; i += 2) this.emitBurst(body[i]!, '#ff7a1a', 2, 0.6, Math.max(3, cs * 0.16), 1.2);
        break;
      }
      case 'thunder': {
        // A forked bolt from the sky onto the head, rings of light, sparks all down the body.
        const pts: Pt[] = [[x + (Math.random() - 0.5) * cs * 2, y - cs * 11]];
        for (let k = 1; k < 10; k++) pts.push([x + (Math.random() - 0.5) * cs * 1.6 * (1 - k / 10), y - cs * 11 * (1 - k / 10)]);
        pts.push([x, y]);
        const [mx, my] = pts[4]!;
        const fork: Pt[] = [[mx, my]];
        for (let k = 1; k <= 3; k++) fork.push([mx + cs * 0.9 * k + (Math.random() - 0.5) * cs * 0.6, my + cs * 0.9 * k]);
        this.fx.push({ kind: 'bolt', x, y, t0, dur: 480, color: '#7fe7ff', size: cs * 1.2, pts });
        this.fx.push({ kind: 'bolt', x, y, t0, dur: 360, color: '#7fe7ff', size: cs * 0.7, pts: fork });
        this.fx.push({ kind: 'ring', x, y, t0, dur: 300, color: '#ffffff', size: cs * 2.4 });
        this.fx.push({ kind: 'ring', x, y, t0: t0 + 80, dur: 480, color: '#7fe7ff', size: cs * 4 });
        for (let i = 0; i < body.length; i++) this.emitBurst(body[i]!, STORM[i % 2]!, 2, 1.6, Math.max(2, cs * 0.14), 0.8);
        break;
      }
      case 'royal': {
        // A crown bursts up out of the eaten snake in a shower of violet and gold stars.
        this.fx.push({ kind: 'crown', x, y, t0, dur: 1000, color: '#ffd166', size: cs });
        this.fx.push({ kind: 'ring', x, y, t0, dur: 420, color: '#ffd166', size: cs * 3 });
        this.fx.push({ kind: 'ring', x, y, t0: t0 + 100, dur: 520, color: '#b388ff', size: cs * 4.2 });
        for (let i = 0; i < 20; i++) {
          const a = Math.random() * Math.PI * 2;
          const v = cs * (0.05 + Math.random() * 0.12);
          this.emitOne(x, y, Math.cos(a) * v, Math.sin(a) * v, STARDUST[i % STARDUST.length]!, Math.max(2, cs * 0.16), 30 + Math.random() * 20, i % 2 ? SHAPE_SQUARE : SHAPE_ROUND);
        }
        break;
      }
    }
  }

  /** Brief full-screen tint, e.g. when you get a kill. */
  flash(color: string, strength = 0.35) {
    if (this.opts.reduceMotion) return;
    this.flashState = { color, k: strength };
  }

  /**
   * A snake crashed: its death effect (see DEATH_EFFECTS), drawn over the next second or so. Only a
   * crash sets one off, so none of them cost anything while snakes are alive.
   */
  emitDeath(body: number[], color: string, isMe: boolean, effect = 'classic') {
    if (isMe && !this.opts.reduceMotion) this.shake = 10;
    if (this.opts.reduceMotion || !body.length) return;
    const head = body[0]!;
    const t0 = this.lastRenderAt;
    const cs = this.cell;
    switch (effect) {
      case 'confetti': {
        // Big coloured flakes from every cell, floating longer than sparks.
        const flake = Math.max(4, cs * 0.3);
        for (let i = 0; i < body.length; i++) {
          for (let k = 0; k < 3; k++) this.emitBurst(body[i]!, CONFETTI[(i * 3 + k) % CONFETTI.length]!, 2, 1.6, flake, 1.5, true);
        }
        break;
      }
      case 'shockwave':
        for (let i = 0; i < body.length; i += 2) this.emitBurst(body[i]!, color, 3, 0.8);
        this.fx.push({ kind: 'ring', x: this.cx(head), y: this.cy(head), t0, dur: 520, color: '#ffffff', size: cs * 4.5 });
        this.fx.push({ kind: 'ring', x: this.cx(head), y: this.cy(head), t0: t0 + 110, dur: 620, color, size: cs * 6 });
        break;
      case 'lightning': {
        const x = this.cx(head);
        const y = this.cy(head);
        // A jagged bolt from well above the head down onto it.
        const pts: Pt[] = [[x + (Math.random() - 0.5) * cs * 2, y - cs * 8]];
        for (let k = 1; k < 8; k++) pts.push([x + (Math.random() - 0.5) * cs * 1.4 * (1 - k / 8), y - cs * 8 * (1 - k / 8)]);
        pts.push([x, y]);
        this.fx.push({ kind: 'bolt', x, y, t0, dur: 420, color: '#7fe7ff', size: cs * 1.6, pts });
        this.fx.push({ kind: 'ring', x, y, t0, dur: 340, color: '#fff6a0', size: cs * 2.6 });
        for (let i = 0; i < body.length; i++) this.emitBurst(body[i]!, i % 2 ? '#fff6a0' : '#7fe7ff', 3, 1.4, Math.max(3, cs * 0.18));
        break;
      }
      case 'fireworks': {
        // Shells bursting one after another at points along the body, each its own colour.
        const shells = Math.min(5, Math.max(2, Math.ceil(body.length / 4)));
        for (let k = 0; k < shells; k++) {
          const cell = body[Math.floor((k / shells) * body.length)]!;
          this.fx.push({ kind: 'burst', x: this.cx(cell), y: this.cy(cell), t0: t0 + k * 130, dur: 0, color: FIREWORKS[k % FIREWORKS.length]!, size: cs, cell });
        }
        break;
      }
      default:
        for (let i = 0; i < body.length; i += 2) this.emitBurst(body[i]!, color, 3, 0.8);
    }
  }

  /** Death effects in flight: rings and bolts drawn until they fade, bursts set off once their time comes. */
  private fx: Array<{ kind: 'ring' | 'bolt' | 'burst' | 'pop' | 'crown'; x: number; y: number; t0: number; dur: number; color: string; size: number; pts?: Pt[]; cell?: number }> = [];

  private drawFx(now: number) {
    if (!this.fx.length) return;
    const ctx = this.ctx;
    let n = 0;
    for (const f of this.fx) {
      const t = (now - f.t0) / Math.max(1, f.dur);
      if (f.kind === 'burst' || f.kind === 'pop') {
        if (now < f.t0) {
          this.fx[n++] = f;
          continue;
        }
        // A firework shell, or a firecracker's smaller pop.
        const big = f.kind === 'burst';
        const spark = Math.max(3, this.cell * (big ? 0.22 : 0.16));
        this.emitBurst(f.cell!, f.color, big ? 22 : 8, big ? 2.6 : 1.8, spark, big ? 1.3 : 0.8);
        this.emitBurst(f.cell!, '#ffffff', big ? 6 : 3, 1.4, spark);
        this.fx.push({ kind: 'ring', x: f.x, y: f.y, t0: now, dur: big ? 360 : 200, color: big ? f.color : '#fff6d6', size: this.cell * (big ? 2.4 : 1.2) });
        continue;
      }
      if (t >= 1) continue;
      this.fx[n++] = f;
      if (t < 0) continue;
      ctx.strokeStyle = f.color;
      if (f.kind === 'crown') {
        // Pops up big, rises and fades.
        const grow = t < 0.2 ? 0.6 + (t / 0.2) * 0.6 : 1.2 - (t - 0.2) * 0.2;
        ctx.globalAlpha = t > 0.6 ? 1 - (t - 0.6) / 0.4 : 1;
        drawCrown(ctx, f.x, f.y - f.size * (0.9 + 1.8 * (1 - (1 - t) * (1 - t))), f.size * 0.8 * grow);
        continue;
      }
      if (f.kind === 'ring') {
        ctx.globalAlpha = 1 - t;
        ctx.lineWidth = Math.max(1, f.size * 0.07 * (1 - t));
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size * (1 - (1 - t) * (1 - t)), 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Flickers as it fades.
        ctx.globalAlpha = (1 - t) * (t < 0.5 && Math.floor(t * 12) % 2 ? 0.35 : 1);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const [w, c] of [
          [f.size * 0.36, f.color],
          [f.size * 0.12, '#ffffff'],
        ] as const) {
          ctx.strokeStyle = c;
          ctx.lineWidth = Math.max(1, w);
          ctx.beginPath();
          ctx.moveTo(...f.pts![0]!);
          for (const q of f.pts!.slice(1)) ctx.lineTo(...q);
          ctx.stroke();
        }
      }
    }
    this.fx.length = n;
    ctx.globalAlpha = 1;
  }

  /** Draw one frame. `alpha` (0..1) is progress from the previous tick to the current one. */
  /** Where your own head was last drawn, in CSS px from the canvas's top-left (mouse steering aims from here). */
  private myHead: { x: number; y: number } | null = null;
  headOnScreen(): { x: number; y: number } | null {
    return this.myHead;
  }

  render(m: StateMirror, alpha: number, now: number, mySlot: number | null, myDir: Dir | null, own: OwnSnake | null = null) {
    const ctx = this.ctx;
    if (!this.gridW) return;
    // Right before drawing, so a canvas wiped by a new size is drawn again before it is ever shown.
    if (this.sizeDirty) this.applySize();
    this.spriteBudget = SPRITE_REDRAWS_PER_FRAME;
    this.own = own;
    this.food = m.food;
    this.frameNo++;
    this.steps = this.lastRenderAt ? Math.min(6, Math.max(0, (now - this.lastRenderAt) / FRAME_60_MS)) : 1;
    this.lastRenderAt = now;
    if (!this.staticLayer && !this.isBigArena) this.buildStatic();
    const cs = this.cell;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = '#05070d';
    ctx.fillRect(0, 0, this.viewW, this.viewH);

    // Camera: centered arena, or follow own head when the arena doesn't fit.
    const arenaW = this.gridW * cs;
    const arenaH = this.gridH * cs;
    let ox = (this.viewW - arenaW) / 2;
    let oy = this.opts.align === 'top' ? Math.min(8, Math.max(0, this.viewH - arenaH)) : (this.viewH - arenaH) / 2;
    if (this.cameraMode) {
      // Players follow themselves; spectators follow their friend, or whoever is leading.
      const me =
        mySlot !== null
          ? m.snakes.find((s) => s.slot === mySlot)
          : (m.snakes.find((s) => s.slot === this.follow && s.alive) ?? [...m.snakes].filter((s) => s.alive).sort((a, b) => b.score - a.score)[0]);
      if (me?.alive && me.body.length) {
        const h = this.headPos(me, alpha);
        const first = !this.lastCamAt;
        const dt = first ? 0 : Math.min(0.1, Math.max(0, now - this.lastCamAt) / 1000);
        this.lastCamAt = now;
        // Centred on the head, with no look-ahead: a look-ahead has to swing the whole view sideways on
        // every turn, which reads as a lurch. Not clamped to the arena either, so the head never slides
        // under the HUD (top players, minimap) in a corner; the dark void past the border shows instead.
        const tx = this.viewW / 2 - h[0];
        const ty = this.viewH / 2 - h[1];
        // Snap after a respawn on the other side of the map instead of panning across it.
        const far = Math.abs(tx - this.camX) > this.viewW || Math.abs(ty - this.camY) > this.viewH;
        if (this.opts.reduceMotion || far || first) {
          this.camX = tx;
          this.camY = ty;
          this.camV.x.x = this.camV.y.x = 0;
        } else {
          this.camX = smoothDamp(this.camX, tx, this.camV.x, FOLLOW_EASE_S, dt);
          this.camY = smoothDamp(this.camY, ty, this.camV.y, FOLLOW_EASE_S, dt);
        }
      }
      ox = arenaW < this.viewW ? ox : this.camX;
      oy = arenaH < this.viewH ? oy : this.camY;
    }
    if (this.shake > 0) {
      ox += (Math.random() - 0.5) * this.shake;
      oy += (Math.random() - 0.5) * this.shake;
      this.shake *= Math.pow(0.85, this.steps);
      if (this.shake < 0.3) this.shake = 0;
    }
    // Whole device pixels: the ground texture then maps 1:1 onto the screen instead of being
    // resampled every frame, which was half of a phone's frame time.
    ox = Math.round(ox * this.dpr) / this.dpr;
    oy = Math.round(oy * this.dpr) / this.dpr;
    ctx.translate(ox, oy);
    if (this.isBigArena) this.drawArenaWindow(ox, oy);
    else ctx.drawImage(this.staticLayer as CanvasImageSource, 0, 0, arenaW, arenaH);
    this.drawMotes(ox, oy, now);

    this.drawObjectives(now);
    this.drawObstacles(m);
    this.drawZone(now);
    // The radar's targets are picked up while drawing the food (see drawRadar): a player's own big-arena view.
    this.radar.on = this.isBigArena && mySlot !== null && this.opts.labels !== false;
    this.radar.golden = this.radar.power = -1;
    this.drawFood(m, now, ox, oy);
    this.drawGulps(now);
    // Big arenas: most snakes are off screen, and drawing one costs several strokes over its whole body.
    const visible = m.snakes.filter((s) => s.alive && s.body.length && (!this.cameraMode || this.onScreen(s, ox, oy)));
    // The arena window on screen (plus a margin for glows and shadows): snakes are drawn only there.
    const margin = cs * 3;
    this.view.x0 = -ox - margin;
    this.view.y0 = -oy - margin;
    this.view.x1 = this.viewW - ox + margin;
    this.view.y1 = this.viewH - oy + margin;
    const ghost = this.ghost?.snake.alive && this.ghost.snake.body.length ? { ...this.ghost.snake, slot: GHOST_SLOT, effects: EFFECT_GHOST } : null;
    if (ghost) this.drawSnake(ghost, this.ghost!.alpha, false, null, now);
    this.pickFancy(visible, mySlot);
    for (const s of visible) if (s.slot !== mySlot) this.drawSnake(s, alpha, false, null, now);
    const me = mySlot !== null ? visible.find((s) => s.slot === mySlot) : undefined;
    if (me) this.drawSnake(me, alpha, true, myDir, now);
    if (me) {
      const h = this.headPos(me, alpha);
      this.myHead = { x: h[0] + ox, y: h[1] + oy };
    } else this.myHead = null;
    this.drawParticles();
    this.drawFx(now);
    for (const s of visible) this.drawLabel(s, alpha, s.slot === mySlot);
    if (ghost) this.drawGhostLabel(ghost, this.ghost!.alpha, this.ghost!.label);
    this.drawMarkers(m, visible, alpha, now, ox, oy, mySlot);
    this.drawRadar(now, ox, oy);
    this.drawTexts();
    if (this.flashState) {
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.globalAlpha = this.flashState.k;
      ctx.fillStyle = this.flashState.color;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
      ctx.globalAlpha = 1;
      this.flashState.k *= Math.pow(0.86, this.steps);
      if (this.flashState.k < 0.01) this.flashState = null;
    }
  }

  /**
   * Radar (see drawRadar): whether it's on this frame, and the nearest golden star and power-up out of view
   * (cell, or -1, and squared distance in px from the middle of the view; the power-up's kind).
   */
  private radar = { on: false, golden: -1, goldenD: 0, power: -1, powerD: 0, powerKind: 0 };

  private radarCandidate(cell: number, kind: number, dx: number, dy: number) {
    const d = dx * dx + dy * dy;
    const reach = RADAR_CELLS * this.cell;
    if (d > reach * reach) return;
    const r = this.radar;
    if (kind === FOOD_GOLDEN) {
      if (r.golden < 0 || d < r.goldenD) {
        r.golden = cell;
        r.goldenD = d;
      }
    } else if (r.power < 0 || d < r.powerD) {
      r.power = cell;
      r.powerD = d;
      r.powerKind = kind;
    }
  }

  /**
   * Radar: a badge circling your head that points at the nearest golden star and the nearest power-up
   * out of view (up to RADAR_CELLS away), fainter the further off they are. Round the head rather than at
   * the screen's edge, where the HUD sits over the arena and would hide it.
   */
  private drawRadar(now: number, ox: number, oy: number) {
    const head = this.myHead;
    const radar = this.radar;
    if (!radar.on || !head || (radar.golden < 0 && radar.power < 0)) return;
    const ctx = this.ctx;
    const cs = this.cell;
    const ring = Math.max(cs * 3.2, Math.min(this.viewW, this.viewH) * 0.3);
    const r = Math.max(12, cs * 0.5);
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const cell of [radar.golden, radar.power]) {
      if (cell < 0) continue;
      const golden = cell === radar.golden;
      const color = golden ? this.opts.theme.golden : POWERUP_STYLE[radar.powerKind]!.color;
      const dx = this.cx(cell) + ox - head.x;
      const dy = this.cy(cell) + oy - head.y;
      const ang = Math.atan2(dy, dx);
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const x = head.x + cos * ring;
      const y = head.y + sin * ring;
      const pulse = this.opts.reduceMotion ? 1 : 0.85 + 0.15 * Math.sin(now / 240);
      ctx.globalAlpha = Math.max(0.55, Math.min(0.95, 1.3 - Math.hypot(dx, dy) / cs / RADAR_CELLS)) * pulse;
      // Pointer just outside the badge, toward the target.
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x + cos * r * 1.75, y + sin * r * 1.75);
      ctx.lineTo(x + Math.cos(ang + 0.5) * r * 1.05, y + Math.sin(ang + 0.5) * r * 1.05);
      ctx.lineTo(x + Math.cos(ang - 0.5) * r * 1.05, y + Math.sin(ang - 0.5) * r * 1.05);
      ctx.closePath();
      ctx.fill();
      // A dark disc ringed in the pickup's color, with its star or glyph inside.
      ctx.fillStyle = 'rgba(10,14,30,0.82)';
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1.5, r * 0.16);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      const pu = POWERUP_STYLE[radar.powerKind];
      if (golden) this.star(x, y, r * 0.62, 0);
      else if (pu?.icon) this.drawEmoji(pu.icon, x, y, r * 1.25);
      else {
        ctx.font = `800 ${Math.round(r * 1.05)}px "Space Grotesk", system-ui, sans-serif`;
        ctx.fillText(pu!.glyph, x, y + r * 0.05);
      }
    }
    ctx.restore();
  }

  /** Each mote: where it starts (0..1 of the view), how deep it sits (parallax), its size, and its twinkle's phase. */
  private motes: Float32Array | null = null;

  /**
   * Ambient specks drifting slowly up over the board, twinkling. They lag the camera a little, as if
   * they hung below a glass floor, which gives the arena depth. High quality only, and off with reduced motion.
   */
  private drawMotes(ox: number, oy: number, now: number) {
    if (this.opts.quality !== 'high' || this.opts.reduceMotion) return;
    if (!this.motes) {
      this.motes = new Float32Array(MOTES * 5);
      for (let i = 0; i < MOTES; i++) {
        this.motes[i * 5] = Math.random();
        this.motes[i * 5 + 1] = Math.random();
        this.motes[i * 5 + 2] = 0.55 + Math.random() * 0.35;
        this.motes[i * 5 + 3] = 1 + Math.random() * 2.2;
        this.motes[i * 5 + 4] = Math.random() * Math.PI * 2;
      }
    }
    const ctx = this.ctx;
    const pad = 40;
    const W = this.viewW + pad * 2;
    const H = this.viewH + pad * 2;
    const sprite = this.spark(mix(this.opts.theme.border, '#ffffff', 0.55)) as CanvasImageSource;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const m = this.motes;
    for (let i = 0; i < MOTES; i++) {
      const depth = m[i * 5 + 2]!;
      const size = m[i * 5 + 3]!;
      const phase = m[i * 5 + 4]!;
      // Drift up and a little sideways; the camera moves them by `depth` of its own motion.
      const x = (((m[i * 5]! * W + ox * depth + Math.sin(now / 3100 + phase) * 14) % W) + W) % W - pad;
      const y = (((m[i * 5 + 1]! * H + oy * depth - now * 0.008 * depth) % H) + H) % H - pad;
      ctx.globalAlpha = 0.12 + 0.3 * (0.5 + 0.5 * Math.sin(now / 900 + phase * 3));
      const r = size * 2.2;
      ctx.drawImage(sprite, x - r, y - r, r * 2, r * 2);
    }
    ctx.restore();
  }

  private drawObjectives(now: number) {
    const { hill, hillMoving, bases } = this.objectives;
    const ctx = this.ctx;
    const cs = this.cell;
    bases.forEach((b, team) => {
      const color = TEAM_COLORS[team] ?? '#fff';
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.14;
      ctx.fillRect(b.x * cs, b.y * cs, b.w * cs, b.h * cs);
      ctx.globalAlpha = 0.85;
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, cs * 0.14);
      ctx.setLineDash([cs * 0.45, cs * 0.3]);
      ctx.strokeRect(b.x * cs, b.y * cs, b.w * cs, b.h * cs);
      ctx.setLineDash([]);
      ctx.globalAlpha = 0.6;
      ctx.font = `800 ${Math.round(cs * 1.4)}px "Space Grotesk", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚑', (b.x + b.w / 2) * cs, (b.y + b.h / 2) * cs);
      ctx.globalAlpha = 1;
    });
    if (hill) {
      const pulse = this.opts.reduceMotion ? 0.5 : 0.5 + Math.sin(now / 200) * 0.25;
      ctx.fillStyle = TROPHY_COLOR;
      ctx.globalAlpha = 0.1 + 0.08 * pulse;
      ctx.fillRect(hill.x * cs, hill.y * cs, hill.w * cs, hill.h * cs);
      ctx.globalAlpha = hillMoving ? 0.4 + 0.5 * pulse : 0.9;
      ctx.strokeStyle = TROPHY_COLOR;
      ctx.lineWidth = Math.max(2, cs * 0.16);
      if (hillMoving) ctx.setLineDash([cs * 0.4, cs * 0.3]);
      ctx.strokeRect(hill.x * cs, hill.y * cs, hill.w * cs, hill.h * cs);
      ctx.setLineDash([]);
      ctx.globalAlpha = 0.35;
      this.drawEmoji('crown', (hill.x + hill.w / 2) * cs, (hill.y + hill.h / 2) * cs, cs * 1.8);
      ctx.globalAlpha = 1;
    }
  }

  private drawObstacles(m: StateMirror) {
    if (!m.obstacles.size) return;
    const ctx = this.ctx;
    const cs = this.cell;
    const r = cs * 0.22;
    for (const cell of m.obstacles) {
      const x = (cell % this.gridW) * cs + cs * 0.06;
      const y = Math.floor(cell / this.gridW) * cs + cs * 0.06;
      const size = cs * 0.88;
      const g = ctx.createLinearGradient(x, y, x + size, y + size);
      g.addColorStop(0, '#5a6390');
      g.addColorStop(1, '#262b45');
      ctx.fillStyle = g;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, size, size, r);
      else ctx.rect(x, y, size, size);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // bevel highlight
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(x + size * 0.2, y + size * 0.16, size * 0.36, Math.max(1, size * 0.1));
    }
  }

  private drawZone(now: number) {
    const { inset, warn, ring } = this.zone;
    if (!inset && !warn) return;
    const ctx = this.ctx;
    const cs = this.cell;
    const W = this.gridW * cs;
    const H = this.gridH * cs;
    const i = inset * cs;
    if (inset) {
      // Everything outside the zone is dimmed with a red tint.
      ctx.fillStyle = 'rgba(120,10,30,0.42)';
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      ctx.rect(i, i, W - 2 * i, H - 2 * i);
      ctx.fill('evenodd');
    }
    const blink = this.opts.reduceMotion ? 1 : 0.55 + Math.sin(now / 90) * 0.45;
    if (warn) {
      const j = (inset + ring) * cs;
      ctx.strokeStyle = `rgba(255,80,80,${0.35 + 0.4 * blink})`;
      ctx.lineWidth = Math.max(2, cs * 0.12);
      ctx.setLineDash([cs * 0.5, cs * 0.35]);
      ctx.strokeRect(j, j, W - 2 * j, H - 2 * j);
      ctx.setLineDash([]);
    }
    if (inset) {
      ctx.strokeStyle = '#ff4d6d';
      ctx.lineWidth = Math.max(2, cs * 0.16);
      ctx.strokeRect(i, i, W - 2 * i, H - 2 * i);
    }
  }

  private drawTexts() {
    if (!this.texts.length) return;
    const ctx = this.ctx;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const t of this.texts) {
      t.life += this.steps;
      const k = t.life / t.max;
      const rise = this.opts.reduceMotion ? 0 : this.cell * 1.6 * (1 - (1 - k) * (1 - k));
      const scale = this.opts.reduceMotion ? 1 : k < 0.15 ? 0.6 + (k / 0.15) * 0.5 : 1.1 - Math.min(0.1, (k - 0.15) * 0.3);
      ctx.globalAlpha = k > 0.6 ? Math.max(0, 1 - (k - 0.6) / 0.4) : 1;
      ctx.font = `800 ${Math.round(Math.max(13, this.cell * 0.85) * t.size * scale)}px "Space Grotesk", system-ui, sans-serif`;
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(5,7,13,0.75)';
      ctx.strokeText(t.text, t.x, t.y - this.cell * 0.6 - rise);
      ctx.fillStyle = t.color;
      ctx.fillText(t.text, t.x, t.y - this.cell * 0.6 - rise);
    }
    ctx.globalAlpha = 1;
    this.texts = this.texts.filter((t) => t.life < t.max);
  }

  /** Any body cell (plus a margin) inside the view. */
  private onScreen(s: MirrorSnake, ox: number, oy: number): boolean {
    const cs = this.cell;
    const x0 = (-ox - cs * 2) / cs;
    const y0 = (-oy - cs * 2) / cs;
    const x1 = (this.viewW - ox + cs * 2) / cs;
    const y1 = (this.viewH - oy + cs * 2) / cs;
    for (const c of s.body) {
      const x = c % this.gridW;
      const y = (c - x) / this.gridW;
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) return true;
    }
    return false;
  }

  /** Orthogonal neighbours, counting the way round a wrap-around edge (false for a teleport). */
  private stepped(a: number, b: number): boolean {
    const W = this.gridW;
    const H = this.gridH;
    const dx = Math.abs((a % W) - (b % W));
    const dy = Math.abs(Math.floor(a / W) - Math.floor(b / W));
    return (dy === 0 && (dx === 1 || dx === W - 1)) || (dx === 0 && (dy === 1 || dy === H - 1));
  }

  /**
   * Cells the head moved forward this tick (2 with speed or boost), if it moved along the grid.
   * 0 means "don't slide" (spawned, teleported).
   */
  private headAdvance(s: MirrorSnake): number {
    const body = s.body;
    const was = s.prevBody[0];
    if (was === undefined || body[0] === was) return 0;
    for (let k = 1; k <= 3 && k < body.length; k++) {
      if (body[k] !== was) continue;
      for (let j = 1; j <= k; j++) if (!this.stepped(body[j - 1]!, body[j]!)) return 0;
      return k;
    }
    return 0;
  }

  /** Per frame: each snake's shape, built once and shared by the body, camera, labels and markers. */
  private shapes = new Map<number, { frame: number; shape: SnakeShape }>();
  private frameNo = 0;
  /** The board's food this frame, for snakes opening their mouths at it. */
  private food: ReadonlyMap<number, unknown> = new Map();
  /** How far each snake's jaws are open (0..1), eased between frames. */
  private mouths = new Map<number, number>();
  /** Per snake: the food straight ahead last frame (to notice it being eaten), and meals going down its body. */
  private meals = new Map<number, { ahead: number; kind: number; bulges: Array<{ d: number; size: number }> }>();
  /** Food on its way into a mouth, and the cells it left (their food isn't drawn there meanwhile). */
  private gulps: Array<{ cell: number; kind: number; slot: number; start: number }> = [];
  private gulped = new Set<number>();
  /** Where each snake's head was drawn last frame: the gulps fly to it. */
  private heads = new Map<number, Pt>();
  /** When each snake last ate (its head chomps, see CHOMP_MS). */
  private chomps = new Map<number, number>();
  /** Where each snake's eyes look (unit vector), eased between frames. */
  private looks = new Map<number, Pt>();

  /** How far through its chomp a snake's head is (0..1), or undefined when it isn't chomping. */
  private chompOf(slot: number, now: number): number | undefined {
    const at = this.chomps.get(slot);
    if (at === undefined) return undefined;
    const t = (now - at) / CHOMP_MS;
    return t >= 0 && t < 1 ? t : undefined;
  }

  /**
   * Where a snake's eyes look: at the nearest food up to LOOK_CELLS away that isn't behind it, else
   * straight ahead. The eyes swing over to it in about a tenth of a second rather than jumping.
   */
  private lookOf(s: MirrorSnake, heading: Pt, hx: number, hy: number): Pt {
    let tx = heading[0];
    let ty = heading[1];
    const head = (this.ownFor(s)?.snake ?? s).body[0];
    if (head !== undefined && s.slot !== GHOST_SLOT && this.food.size) {
      const w = this.gridW;
      const h = this.gridH;
      const x0 = head % w;
      const y0 = Math.floor(head / w);
      let best = Infinity;
      for (let dy = -LOOK_CELLS; dy <= LOOK_CELLS; dy++) {
        for (let dx = -LOOK_CELLS; dx <= LOOK_CELLS; dx++) {
          const d2 = dx * dx + dy * dy;
          if (!d2 || d2 >= best || dx * heading[0] + dy * heading[1] < 0) continue;
          const x = x0 + dx;
          const y = y0 + dy;
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          const cell = y * w + x;
          if (!this.food.has(cell) || this.gulped.has(cell)) continue;
          best = d2;
          const fx = this.cx(cell) - hx;
          const fy = this.cy(cell) - hy;
          const l = Math.hypot(fx, fy) || 1;
          tx = fx / l;
          ty = fy / l;
        }
      }
    }
    let cur = this.looks.get(s.slot);
    if (!cur) {
      cur = [tx, ty];
      this.looks.set(s.slot, cur);
    } else if (this.opts.reduceMotion) {
      cur[0] = tx;
      cur[1] = ty;
    } else {
      const k = 1 - Math.exp((-this.steps * FRAME_60_MS) / 90);
      cur[0] += (tx - cur[0]) * k;
      cur[1] += (ty - cur[1]) * k;
    }
    return cur;
  }

  /**
   * Jaws part as food comes up ahead (wide one cell away, a little two away) and snap shut once it's
   * eaten: opening takes about a tenth of a second, closing a third of that. Looks only along the way
   * the snake is going, so it costs two map lookups.
   */
  private mouthOpen(s: MirrorSnake, dir: Dir): number {
    if (s.slot === GHOST_SLOT) return 0;
    const own = this.ownFor(s);
    const head = (own?.snake ?? s).body[0];
    const meal = this.mealOf(s.slot);
    meal.ahead = -1;
    let target = 0;
    if (head !== undefined) {
      const w = this.gridW;
      const h = this.gridH;
      for (let k = 1; k <= 2 && !target; k++) {
        const x = (((head % w) + DX[dir] * k) % w + w) % w;
        const y = ((Math.floor(head / w) + DY[dir] * k) % h + h) % h;
        const cell = y * w + x;
        const kind = this.food.get(cell);
        if (kind === undefined || own?.reached.has(cell)) continue;
        target = k === 1 ? 1 : 0.45;
        if (k === 1) {
          meal.ahead = cell;
          meal.kind = kind as number;
        }
      }
    }
    const cur = this.mouths.get(s.slot) ?? 0;
    const step = (this.steps * FRAME_60_MS) / (target > cur ? 110 : 35);
    const next = this.opts.reduceMotion ? target : target > cur ? Math.min(target, cur + step) : Math.max(target, cur - step);
    this.mouths.set(s.slot, next);
    return next;
  }
  /** Your own snake's predicted view online, when `s` is your snake. */
  private ownFor(s: MirrorSnake): OwnSnake | null {
    return this.own && s.slot === this.own.snake.slot ? this.own : null;
  }

  private mealOf(slot: number) {
    let m = this.meals.get(slot);
    if (!m) {
      m = { ahead: -1, kind: 0, bulges: [] };
      this.meals.set(slot, m);
    }
    return m;
  }

  /**
   * Notice a snake eating (its head is now where the food straight ahead was: moving onto food always
   * eats it), then send the meal down its body as a bulge and draw the food flying into its mouth.
   * Returns the bulges to draw. Off with reduced motion.
   *
   * Not "and the food is gone": your own snake online is drawn from its prediction, a tick or more ahead
   * of the server, so its food is still on the board when its head gets there.
   */
  private swallow(s: MirrorSnake, now: number): ReadonlyArray<{ d: number; size: number }> | undefined {
    if (s.slot === GHOST_SLOT || this.opts.reduceMotion) return undefined;
    const own = this.ownFor(s);
    const body = (own?.snake ?? s).body;
    const head = body[0];
    const meal = this.mealOf(s.slot);
    if (meal.ahead >= 0 && head === meal.ahead) {
      meal.bulges.push({ d: 0, size: 0 });
      if (meal.bulges.length > BULGE_MAX) meal.bulges.shift();
      this.chomps.set(s.slot, now);
      if (isEdible(meal.kind as FoodKind)) {
        this.gulps.push({ cell: head, kind: meal.kind, slot: s.slot, start: now });
        this.gulped.add(head);
      }
      meal.ahead = -1;
    }
    const bulges = meal.bulges;
    const len = body.length;
    if (len > BULGE_MAX_LENGTH) bulges.length = 0;
    if (!bulges.length) return undefined;
    // Fast on a long snake; on a short one slower, so the meal is still seen going down (BULGE_MIN_MS).
    const speed = Math.min(BULGE_CELLS_PER_S, (len * 1000) / BULGE_MIN_MS);
    const travel = (this.steps * FRAME_60_MS * speed) / 1000;
    let n = 0;
    for (const b of bulges) {
      b.d += travel;
      if (b.d >= len - 0.5) continue;
      // Swells in over the first half cell, shrinks away over the last two.
      b.size = Math.min(1, b.d / 0.5) * Math.min(1, (len - 0.5 - b.d) / 2);
      bulges[n++] = b;
    }
    bulges.length = n;
    return n ? bulges : undefined;
  }

  /** Eaten food flying into the mouth that ate it, shrinking as it goes (drawn under the snakes, so the head swallows it). */
  private drawGulps(now: number) {
    if (!this.gulps.length) return;
    const ctx = this.ctx;
    const cs = this.cell;
    let n = 0;
    for (const g of this.gulps) {
      const t = (now - g.start) / GULP_MS;
      if (t >= 1 || t < 0) {
        this.gulped.delete(g.cell);
        continue;
      }
      this.gulps[n++] = g;
      const fx = this.cx(g.cell);
      const fy = this.cy(g.cell);
      const to = this.heads.get(g.slot);
      // Slow to start, then sucked in fast.
      const e = t * t;
      const x = to ? fx + (to[0] - fx) * e : fx;
      const y = to ? fy + (to[1] - fy) * e : fy;
      const scale = 1 - 0.75 * t;
      ctx.globalAlpha = 1 - 0.4 * t;
      if (g.kind === FOOD_GOLDEN) {
        ctx.fillStyle = this.opts.theme.golden;
        this.star(x, y, cs * 0.88 * scale, 0);
      } else {
        const normal = g.kind !== FOOD_REMAINS_BIG && g.kind !== FOOD_REMAINS;
        const glow = normal && this.opts.quality === 'high' && !isLight(this.opts.theme.background);
        this.drawEmoji(fruitAt(g.cell), x, y, cs * (g.kind === FOOD_REMAINS_BIG ? BIG_REMAINS_SIZE : normal ? FOOD_SIZE : REMAINS_SIZE), scale, glow);
      }
    }
    this.gulps.length = n;
    ctx.globalAlpha = 1;
  }

  /**
   * Time since the last frame, in 60 Hz frames (1 at 60 fps, 0.5 at 120, 2 at 30). Effects that
   * age or fade each frame (particles, popups, shake, flash) go by this, so they last as long on
   * a slow phone or a 120 Hz screen as at 60 fps.
   */
  private steps = 1;
  private lastRenderAt = 0;
  /** This frame's predicted own snake, if any. */
  private own: OwnSnake | null = null;
  /**
   * Per tick: each snake's centre-line points. They only change when a tick lands, and reusing
   * the same array lets the art cache the curve built on it, so frames in between allocate
   * almost nothing (garbage-collection pauses read as stutter).
   */
  private paths = new Map<
    number,
    { body: number[]; prev: number[]; len: number; head: number | undefined; cell: number; pts: Pt[]; breaks: Set<number>; k: number; dropped: number }
  >();

  /**
   * The snake's centre line for this frame: every body cell, plus the old tail cells dropped
   * this tick (sliding away), with breaks across wrap edges.
   *
   * The head is drawn headLag cells behind where it really is (see TURN_FEEL). That lag is what makes
   * turns smooth: by the time the drawn head reaches a cell, the next cell is already known,
   * so it can follow the rounded corner instead of driving into the cell and turning on the spot.
   */
  private snakeShape(s: MirrorSnake, alpha: number): SnakeShape {
    // Your own snake online is drawn where it's predicted to be (camera, body, label and markers alike).
    if (this.own && s.slot === this.own.snake.slot) {
      s = this.own.snake;
      alpha = this.own.alpha;
    }
    const hit = this.shapes.get(s.slot);
    if (hit && hit.frame === this.frameNo) return hit.shape;
    let p = this.paths.get(s.slot);
    if (!p || p.body !== s.body || p.prev !== s.prevBody || p.len !== s.body.length || p.head !== s.body[0] || p.cell !== this.cell) {
      const body = s.body;
      const prev = s.prevBody;
      // Tail cells that left the body this tick (moves and boost trims). More than a few at once
      // (respawn, electric shock) is a jump, not a slide, so those just disappear.
      let path = body;
      let dropped = 0;
      const was = prev[0];
      const moved = was === undefined ? -1 : body.indexOf(was);
      if (moved >= 0 && moved <= 3) {
        const kept = body.length - moved;
        const gone = prev.length - kept;
        if (gone > 0 && gone <= 3 && this.stepped(body[body.length - 1]!, prev[kept]!)) {
          let ok = true;
          for (let i = kept + 1; i < prev.length && ok; i++) ok = this.stepped(prev[i - 1]!, prev[i]!);
          if (ok) {
            path = body.concat(prev.slice(kept));
            dropped = gone;
          }
        }
      }
      const breaks = new Set<number>();
      for (let i = 1; i < path.length; i++) if (!this.adjacent(path[i - 1]!, path[i]!)) breaks.add(i);
      p = {
        body,
        prev,
        len: body.length,
        head: body[0],
        cell: this.cell,
        pts: path.map((c) => [this.cx(c), this.cy(c)]),
        breaks,
        k: this.headAdvance(s),
        dropped,
      };
      this.paths.set(s.slot, p);
    }
    const shape: SnakeShape = { pts: p.pts, breaks: p.breaks, front: this.drawnFront(s.slot, p, p.k * (1 - alpha) + this.feel.headLag), back: p.pts.length - 1 - p.dropped * alpha };
    this.shapes.set(s.slot, { frame: this.frameNo, shape });
    return shape;
  }

  /** Where each snake's head was drawn last frame (see drawnFront). */
  private fronts = new Map<number, { path: object; head: number | undefined; u: number; sched: number }>();

  /**
   * Where along the path to draw the head, given where the tick puts it (`sched`): at an even
   * speed, a little behind, so corners don't slow it down (see TURN_FEEL). Both are fractional
   * indexes into the path, and distances along it are measured in cells.
   */
  private drawnFront(slot: number, p: { pts: Pt[]; breaks: Set<number>; body: number[] }, sched: number): number {
    const path = { pts: p.pts, breaks: p.breaks };
    const cs = this.cell;
    let f = this.fronts.get(slot);
    if (f && f.path !== p) {
      // A new tick: the path now starts `shift` cells further on, so carry the drawn head over.
      const shift = f.head === undefined ? -1 : p.body.indexOf(f.head);
      if (shift >= 0 && shift <= 3) Object.assign(f, { path: p, head: p.body[0], u: f.u + shift, sched: f.sched + shift });
      else f = undefined;
    }
    const rs = snakeDistance(path, sched, cs);
    const step = f ? f.sched - sched : 0;
    if (!f || step < -0.01 || step > 3) {
      // New, respawned, teleported or jumped: start the usual distance behind.
      const u = snakeIndexAt(path, rs + this.feel.slack, cs);
      this.fronts.set(slot, { path: p, head: p.body[0], u, sched });
      return u;
    }
    // How far behind the drawn head was last frame (both where they were then).
    const rd = snakeDistance(path, f.u, cs);
    const lag = rd - snakeDistance(path, f.sched, cs);
    f.sched = sched;
    // The tick moved `step` cells' worth: go that far along the path, a little faster or slower to keep the slack.
    const rate = 1 + Math.max(-CORNER_MAX_RATE, Math.min(CORNER_MAX_RATE, CORNER_EASE * (lag - this.feel.slack)));
    f.u = snakeIndexAt(path, Math.max(rs, Math.min(rs + this.feel.maxLag, rd - step * rate)), cs);
    return f.u;
  }

  /** Where the (drawn) head is this frame. */
  private headPos(s: MirrorSnake, alpha: number): Pt {
    const shape = this.snakeShape(s, alpha);
    return snakePoint(shape, shape.front ?? 0);
  }

  /** Electric crackle: jittery bright bolts along the body and sparks around the head. */
  private drawShock(pts: Pt[], until: number, now: number) {
    if (this.opts.reduceMotion) return;
    const ctx = this.ctx;
    const cs = this.cell;
    const fade = Math.min(1, (until - now) / 400);
    const jitter = cs * 0.45;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'miter';
    for (const [color, width, a] of [
      ['#7fe7ff', cs * 0.22, 0.45],
      ['#fffbd1', cs * 0.09, 0.95],
    ] as const) {
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1, width);
      ctx.globalAlpha = a * fade * (0.6 + Math.random() * 0.4);
      ctx.beginPath();
      for (let i = 0; i < pts.length; i++) {
        const [x, y] = pts[i]!;
        const jx = x + (Math.random() - 0.5) * jitter;
        const jy = y + (Math.random() - 0.5) * jitter;
        if (i) ctx.lineTo(jx, jy);
        else ctx.moveTo(jx, jy);
      }
      ctx.stroke();
    }
    const [hx, hy] = pts[0]!;
    ctx.strokeStyle = '#ffe94d';
    ctx.lineWidth = Math.max(1, cs * 0.08);
    ctx.globalAlpha = fade;
    for (let i = 0; i < 4; i++) {
      const a = Math.random() * Math.PI * 2;
      const r1 = cs * 0.6;
      const r2 = cs * (1 + Math.random() * 0.6);
      ctx.beginPath();
      ctx.moveTo(hx + Math.cos(a) * r1, hy + Math.sin(a) * r1);
      ctx.lineTo(hx + Math.cos(a + 0.3) * (r1 + r2) / 2, hy + Math.sin(a - 0.3) * (r1 + r2) / 2);
      ctx.lineTo(hx + Math.cos(a) * r2, hy + Math.sin(a) * r2);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** The visible part of the arena this frame, in arena px (with a margin). */
  private view = { x0: 0, y0: 0, x1: 0, y1: 0 };

  /** First to last point of a snake's path that lies in the view (null when none does). */
  private clipOf(pts: Pt[]): [number, number] | null {
    const v = this.view;
    let lo = -1;
    let hi = -1;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      if (p[0] < v.x0 || p[0] > v.x1 || p[1] < v.y0 || p[1] > v.y1) continue;
      if (lo < 0) lo = i;
      hi = i;
    }
    // Half a cell either way: the body between a point and its neighbour belongs to both.
    return lo < 0 ? null : [lo - 1, hi + 1];
  }

  /** Other snakes drawn in full this frame (see pickFancy); the rest get their skin's plain look. */
  private fancy = new Set<number>();
  private plainSkins = new Map<string, SkinDef>();

  /**
   * Only the few snakes nearest you wear their skin's costly living pattern and trail (FANCY_OTHERS, none
   * at low quality); farther ones are drawn plain. Your own snake always is in full. A crowd of Legendary
   * snakes on screen then costs about what a few do.
   */
  private pickFancy(visible: MirrorSnake[], mySlot: number | null) {
    this.fancy.clear();
    const n = this.opts.quality === 'low' ? 0 : FANCY_OTHERS;
    if (!n) return;
    const me = mySlot !== null ? this.heads.get(mySlot) : undefined;
    const cx = me ? me[0] : (this.gridW * this.cell) / 2;
    const cy = me ? me[1] : (this.gridH * this.cell) / 2;
    const near = visible
      .filter((s) => s.slot !== mySlot)
      .map((s) => ({ slot: s.slot, d: (this.cx(s.body[0]!) - cx) ** 2 + (this.cy(s.body[0]!) - cy) ** 2 }))
      .sort((a, b) => a.d - b.d);
    for (let i = 0; i < Math.min(n, near.length); i++) this.fancy.add(near[i]!.slot);
  }

  /** A skin without its costly living pattern: signature skins keep colors and head, galaxy loses its stars. */
  private plainSkin(skin: SkinDef): SkinDef {
    if (!skin.signature && skin.pattern !== 'galaxy') return skin;
    let p = this.plainSkins.get(skin.id);
    if (!p) {
      p = { ...skin, pattern: skin.pattern === 'dragon' ? 'scales' : 'solid', signature: undefined };
      this.plainSkins.set(skin.id, p);
    }
    return p;
  }

  private drawSnake(s: MirrorSnake, alpha: number, isMe: boolean, myDir: Dir | null, now: number) {
    const ctx = this.ctx;
    const info = this.players.get(s.slot);
    const own = info?.skin ?? findSkin('emerald');
    const skin = isMe || this.fancy.has(s.slot) || !this.cameraMode ? own : this.plainSkin(own);
    const cs = this.cell;
    if (!s.body.length) return;
    const full = this.snakeShape(s, alpha);
    const clip = this.cameraMode ? this.clipOf(full.pts) : null;
    const shape = clip ? { ...full, clip } : full;
    const [hx, hy] = snakePoint(shape, shape.front ?? 0);
    const seen = this.heads.get(s.slot);
    if (seen) {
      seen[0] = hx;
      seen[1] = hy;
    } else this.heads.set(s.slot, [hx, hy]);
    const fx = s.effects ?? 0;
    if (fx & EFFECT_MAGNET) {
      ctx.strokeStyle = POWERUP_STYLE[FOOD_MAGNET]!.color;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = Math.max(1, cs * 0.08);
      ctx.setLineDash([cs * 0.3, cs * 0.25]);
      ctx.beginPath();
      ctx.arc(hx, hy, cs * (this.magnetRadius + 0.5), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
    if (fx & EFFECT_ARMOR) {
      ctx.strokeStyle = POWERUP_STYLE[FOOD_ARMOR]!.color;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = Math.max(2, cs * 0.12);
      ctx.beginPath();
      ctx.arc(hx, hy, cs * 0.92, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    if (fx & EFFECT_SHIELD) {
      ctx.strokeStyle = '#7fe7ff';
      ctx.globalAlpha = 0.35 + 0.25 * Math.sin(performance.now() / 120);
      ctx.lineWidth = Math.max(2, cs * 0.14);
      ctx.beginPath();
      ctx.arc(hx, hy, cs * 1.05, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // Ghosts (and shielded snakes) are drawn translucent.
    const baseAlpha = fx & EFFECT_GHOST ? 0.42 : fx & EFFECT_SHIELD ? 0.6 : 1;
    ctx.globalAlpha = baseAlpha;

    const burning = !!(fx & EFFECT_FIRE);
    const aura = burning
      ? // Fire: a flickering orange blaze round the whole body, the clearest warning there is.
        { color: '#ffa31a', alpha: this.opts.reduceMotion ? 0.85 : 0.78 + 0.18 * Math.sin(now / 70 + s.slot), width: 2 }
      : fx & EFFECT_FROZEN
        ? { color: POWERUP_STYLE[FOOD_FREEZE]!.color, alpha: 0.7, width: 1.55 }
        : fx & EFFECT_SPEED
          ? { color: skin.glow, alpha: 0.5, width: 1.5 }
          : fx & EFFECT_DOUBLE
            ? { color: POWERUP_STYLE[FOOD_DOUBLE]!.color, alpha: 0.6, width: 1.7 }
            : undefined;
    // The head is fixed to the neck: it looks along the body curve and turns only as the body
    // does (turning it on the spot toward a pending turn read as the neck twisting), smoothed
    // over a little of the curve so a corner swings it round instead of snapping it.
    const heading = snakeHeading(shape, shape.front ?? 0, this.feel.lookAhead, this.feel.lookBehind);
    drawSnakeArt(ctx, shape, {
      skin,
      // The boss is drawn a size up (its body still fills one cell a segment, like everyone's).
      cs: info?.boss ? cs * BOSS_SCALE : cs,
      quality: this.opts.quality,
      reduceMotion: this.opts.reduceMotion,
      now: performance.now(),
      dir: myDir ?? s.dir,
      heading,
      // Swallowing first: it checks the food ahead as it was last frame, before the mouth looks again.
      bulges: this.swallow(s, now),
      chomp: this.chompOf(s.slot, now),
      mouth: this.mouthOpen(s, myDir ?? s.dir),
      look: this.lookOf(s, heading, hx, hy),
      glow: isMe || skin.pattern === 'neon' || skin.pattern === 'flame' || !!skin.signature,
      highlight: isMe,
      aura,
      seed: s.slot,
      // The fine scale texture is the costliest layer and hardly shows on other snakes.
      texture: isMe,
      burning,
    });
    // Boosting (or the speed power-up): sparks stream off the tail, or a signature skin's own trail.
    if (fx & EFFECT_SPEED && !this.opts.reduceMotion && full.back !== undefined) {
      const [tx, ty] = snakePoint(full, full.back);
      if (skin.signature) this.emitTrail(skin.signature, tx, ty);
      else if (Math.random() < 0.55 * this.steps) this.emitAt(tx, ty, skin.glow.startsWith('#') ? skin.glow : '#ffffff', 1, 0.35, Math.max(2, cs * 0.16), 0.75, false);
    }
    if (info && this.isTeamMode()) {
      ctx.strokeStyle = TEAM_COLORS[info.team] ?? '#fff';
      ctx.lineWidth = Math.max(1.5, cs * 0.12);
      ctx.beginPath();
      ctx.arc(hx, hy, cs * 0.86, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const shockUntil = this.shocked.get(s.slot);
    if (shockUntil !== undefined) {
      if (shockUntil > now) this.drawShock(snakeSamples(shape, 0.5), shockUntil, now);
      else this.shocked.delete(s.slot);
    }
    if (fx & EFFECT_CARRY) this.drawEmoji('trophy', hx, hy - cs * 1.1, cs * 1.05);
  }

  private teamMode = false;
  setTeamMode(on: boolean) {
    this.teamMode = on;
  }
  private isTeamMode() {
    return this.teamMode;
  }

  private drawGhostLabel(s: MirrorSnake, alpha: number, label: string) {
    const [hx, hy] = this.headPos(s, alpha);
    const ctx = this.ctx;
    ctx.font = `600 ${Math.max(10, Math.min(13, this.cell * 0.75))}px "Inter Variable", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillText(label, hx + 1, hy - this.cell * 0.8 + 1);
    ctx.fillStyle = '#e8ecff';
    ctx.fillText(label, hx, hy - this.cell * 0.8);
    ctx.globalAlpha = 1;
  }

  /** Name tags' font size (px). */
  private labelSize(): number {
    return Math.max(10, Math.min(13, this.cell * 0.75));
  }

  /** How far above the head a name tag's baseline sits: clear of a crown, halo or diya on the head. */
  private labelLift(info: RenderPlayer): number {
    const acc = info.skin.accessory;
    // The boss is drawn a size up, head and crown with it.
    return this.cell * (info.boss ? BOSS_SCALE : 1) * (acc === 'diya' ? 1.75 : acc === 'crown' || acc === 'halo' ? 1.45 : 0.8);
  }

  private drawLabel(s: MirrorSnake, alpha: number, isMe: boolean) {
    const info = this.players.get(s.slot);
    if (!info || this.cell < 8 || this.opts.labels === false) return;
    const [hx, hy] = this.headPos(s, alpha);
    const ctx = this.ctx;
    const [icon, text] = isMe ? [null, '▼'] : nameParts(info.name);
    const fs = this.labelSize();
    ctx.font = `600 ${fs}px "Inter Variable", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const lift = this.labelLift(info);
    // A bot's or the boss's prefix is drawn art in front of the name, the pair centered over the head.
    let tx = hx;
    if (icon) {
      const iz = fs * 1.3;
      const gap = fs * 0.25;
      const w = ctx.measureText(text).width;
      const left = hx - (iz + gap + w) / 2;
      this.drawEmoji(icon, left + iz / 2, hy - lift - fs * 0.55, iz);
      tx = left + iz + gap + w / 2;
    }
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillText(text, tx + 1, hy - lift + 1);
    ctx.fillStyle = isMe ? '#fff' : this.isTeamMode() ? (TEAM_COLORS[info.team] ?? '#fff') : 'rgba(255,255,255,0.85)';
    ctx.fillText(text, tx, hy - lift);
  }

  /** Bounty crown over the leader, and the revenge target (ring + arrow at the screen edge when off screen). */
  private drawMarkers(m: StateMirror, visible: MirrorSnake[], alpha: number, now: number, ox: number, oy: number, mySlot: number | null) {
    const cs = this.cell;
    if (this.bounty) {
      // A clear leader only, like the engine's bounty (a tie for the lead has none).
      let leader: MirrorSnake | null = null;
      let best = 0;
      for (const s of m.snakes) {
        if (!s.alive || s.score < best) continue;
        leader = s.score > best ? s : null;
        best = s.score;
      }
      if (leader && visible.includes(leader)) {
        const [hx, hy] = this.headPos(leader, alpha);
        const bob = this.opts.reduceMotion ? 0 : Math.sin(now / 260) * cs * 0.12;
        // Above the name tag when tags are shown (it used to sit on top of the leader's name).
        const info = this.players.get(leader.slot);
        const above = this.cell >= 8 && info && this.opts.labels !== false ? this.labelLift(info) + this.labelSize() + cs * 0.75 : cs * 1.2;
        this.drawEmoji('crown', hx, hy - above + bob, cs * 1.2);
      }
    }
    const target = this.nemesis !== null && this.nemesis !== mySlot ? m.snakes.find((s) => s.slot === this.nemesis && s.alive && s.body.length) : undefined;
    if (!target) return;
    const ctx = this.ctx;
    const [hx, hy] = this.headPos(target, alpha);
    const pulse = this.opts.reduceMotion ? 0.8 : 0.6 + 0.4 * Math.abs(Math.sin(now / 240));
    if (visible.includes(target)) {
      ctx.strokeStyle = '#ff4d6d';
      ctx.globalAlpha = pulse;
      ctx.lineWidth = Math.max(2, cs * 0.16);
      ctx.setLineDash([cs * 0.35, cs * 0.25]);
      ctx.beginPath();
      ctx.arc(hx, hy, cs * 1.25, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      return;
    }
    if (!this.cameraMode) return;
    // Off screen: an arrow on the edge of the view, pointing at it.
    const sx = hx + ox;
    const sy = hy + oy;
    const cx = this.viewW / 2;
    const cy = this.viewH / 2;
    const ang = Math.atan2(sy - cy, sx - cx);
    const pad = 26;
    const k = Math.min((cx - pad) / Math.max(1e-6, Math.abs(Math.cos(ang))), (cy - pad) / Math.max(1e-6, Math.abs(Math.sin(ang))));
    const ax = cx + Math.cos(ang) * k;
    const ay = cy + Math.sin(ang) * k;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(ax, ay);
    ctx.rotate(ang);
    ctx.globalAlpha = pulse;
    ctx.fillStyle = '#ff4d6d';
    ctx.beginPath();
    ctx.moveTo(14, 0);
    ctx.lineTo(-6, -10);
    ctx.lineTo(-2, 0);
    ctx.lineTo(-6, 10);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /** Where this frame's food glints are (x, y, size triples), drawn together after the food. */
  private glints = new Float32Array(3 * 64);

  private drawFood(m: StateMirror, now: number, ox: number, oy: number) {
    const ctx = this.ctx;
    const cs = this.cell;
    const { theme, reduceMotion } = this.opts;
    const pulse = reduceMotion ? 1 : 1 + Math.sin(now / 180) * 0.08;
    const cull = this.isBigArena;
    const high = this.opts.quality === 'high';
    // Food glows on dark boards; on light ones a glow is just a stain, so it gets a shadow instead.
    const glow = high && !isLight(theme.background);
    let glints = 0;
    // Forget food that's gone (every so often; it's only used for the pop-in).
    if (this.foodBorn.size > m.food.size + 64) for (const c of this.foodBorn.keys()) if (!m.food.has(c)) this.foodBorn.delete(c);
    // forEach, not for-of over entries: that made a [cell, kind] array per food per frame.
    m.food.forEach((kind, cell) => {
      // Your snake's head has already been here in its prediction: the food is as good as eaten.
      if (this.own?.reached.has(cell) || this.gulped.has(cell)) return;
      let born = this.foodBorn.get(cell);
      if (born === undefined) {
        born = now;
        this.foodBorn.set(cell, now);
      }
      const x = this.cx(cell);
      const y = this.cy(cell);
      if (cull && (x + ox < -cs || y + oy < -cs || x + ox > this.viewW + cs || y + oy > this.viewH + cs)) {
        if (this.radar.on && (kind === FOOD_GOLDEN || POWERUP_STYLE[kind])) this.radarCandidate(cell, kind, x + ox - this.viewW / 2, y + oy - this.viewH / 2);
        return;
      }
      // New food pops in with a little overshoot.
      const t = reduceMotion ? 1 : Math.min(1, (now - born) / 280);
      const pop = t >= 1 ? 1 : Math.max(0.01, 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2));
      const pu = POWERUP_STYLE[kind];
      if (kind === FOOD_TROPHY) {
        ctx.fillStyle = TROPHY_COLOR;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(x, y, cs * 1.25 * pulse, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        this.drawEmoji('trophy', x, y, cs * 1.45);
      } else if (pu) {
        ctx.fillStyle = pu.color;
        ctx.globalAlpha = 0.28;
        ctx.beginPath();
        ctx.arc(x, y, cs * 1.25 * pulse, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = pu.color;
        ctx.lineWidth = Math.max(1.5, cs * 0.1);
        ctx.fillStyle = 'rgba(10,14,30,0.85)';
        ctx.beginPath();
        ctx.arc(x, y, cs * 0.84, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (pu.icon) this.drawEmoji(pu.icon, x, y, cs * 1.12);
        else {
          ctx.fillStyle = pu.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `800 ${Math.round(cs * 0.95)}px "Space Grotesk", system-ui, sans-serif`;
          ctx.fillText(pu.glyph, x, y + cs * 0.03);
        }
      } else if (kind === FOOD_GOLDEN) {
        if (this.opts.quality === 'high') {
          ctx.fillStyle = theme.golden;
          ctx.globalAlpha = 0.25;
          ctx.beginPath();
          ctx.arc(x, y, cs * 1.05 * pulse, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        // Rotating light rays behind the star.
        if (this.opts.quality === 'high') {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(reduceMotion ? 0 : now / 1400);
          ctx.fillStyle = theme.golden;
          ctx.globalAlpha = 0.22;
          for (let i = 0; i < 8; i++) {
            ctx.rotate(Math.PI / 4);
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(-cs * 0.14, -cs * 1.25);
            ctx.lineTo(cs * 0.14, -cs * 1.25);
            ctx.closePath();
            ctx.fill();
          }
          ctx.restore();
          ctx.globalAlpha = 1;
        }
        ctx.fillStyle = theme.golden;
        this.star(x, y, cs * 0.88 * pulse * pop, reduceMotion ? 0 : now / 600);
      } else {
        // Fruit sits still on its cell, so whatever touches the head is what gets eaten.
        const bob = 0;
        // Left-behind food blinks before it disappears.
        const left = kind === FOOD_REMAINS || kind === FOOD_REMAINS_BIG ? (REMAINS_LIFETIME_TICKS * 1000) / this.tickRate - (now - born) : Infinity;
        // A slow, shallow pulse: lots of remains blinking fast read as the whole screen flickering.
        if (left < REMAINS_BLINK_MS) ctx.globalAlpha = reduceMotion ? 0.6 : 0.6 + 0.4 * Math.abs(Math.cos(now / (left < 1500 ? 220 : 400)));
        if (kind === FOOD_REMAINS_BIG) {
          // What a long snake leaves behind: big enough to spot from across the screen.
          ctx.fillStyle = theme.golden;
          ctx.globalAlpha = 0.24 * pulse;
          ctx.beginPath();
          ctx.arc(x, y, cs * BIG_REMAINS_GLOW, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
          this.drawEmoji(fruitAt(cell), x, y + bob, cs * BIG_REMAINS_SIZE, pop);
        } else if (kind === FOOD_REMAINS) {
          this.drawEmoji(fruitAt(cell), x, y + bob, cs * REMAINS_SIZE, pop);
        } else {
          // Soft contact shadow under the fruit (the glow does that job on dark boards).
          if (high && !glow) {
            ctx.fillStyle = 'rgba(0,0,0,0.22)';
            ctx.beginPath();
            ctx.ellipse(x, y + cs * FOOD_SIZE * 0.455, cs * FOOD_SIZE * 0.33 * pop, cs * FOOD_SIZE * 0.1 * pop, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          // Each fruit breathes a little, in its own time, so the board feels alive.
          const phase = phaseAt(cell);
          const breathe = reduceMotion ? 1 : 1 + 0.045 * Math.sin(now / 420 + phase);
          this.drawEmoji(fruitAt(cell), x, y + bob, cs * FOOD_SIZE, pop * breathe, glow);
          // Now and then a glint catches its top left.
          if (high && !reduceMotion && glints < 64) {
            const tw = (now / 2600 + phase / (Math.PI * 2)) % 1;
            if (tw < 0.08) {
              this.glints[glints * 3] = x - cs * FOOD_SIZE * 0.2;
              this.glints[glints * 3 + 1] = y - cs * FOOD_SIZE * 0.23;
              this.glints[glints * 3 + 2] = cs * FOOD_SIZE * 0.2 * Math.sin((tw / 0.08) * Math.PI);
              glints++;
            }
          }
        }
        ctx.globalAlpha = 1;
      }
    });
    if (glints) {
      // Four-point stars, all in one path.
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      for (let i = 0; i < glints; i++) {
        const gx = this.glints[i * 3]!;
        const gy = this.glints[i * 3 + 1]!;
        const r = this.glints[i * 3 + 2]!;
        ctx.moveTo(gx, gy - r);
        ctx.quadraticCurveTo(gx, gy, gx + r, gy);
        ctx.quadraticCurveTo(gx, gy, gx, gy + r);
        ctx.quadraticCurveTo(gx, gy, gx - r, gy);
        ctx.quadraticCurveTo(gx, gy, gx, gy - r);
      }
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  private star(x: number, y: number, r: number, rot: number) {
    const ctx = this.ctx;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = rot + (i * Math.PI) / 5 - Math.PI / 2;
      const rr = i % 2 === 0 ? r : r * 0.45;
      ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();
  }

  private drawParticles() {
    const ctx = this.ctx;
    const p = this.p;
    let n = this.pCount;
    ctx.lineCap = 'round';
    // Particles age and slow down per 60 Hz frame's worth of time, so they look the same at any frame rate.
    const steps = this.steps;
    const drag = Math.pow(0.94, steps);
    // Distance covered over `steps` frames of a speed that drops by 0.94 each frame.
    const travel = (1 - drag) / 0.06;
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      p[o + 4]! += steps;
      if (p[o + 4]! >= p[o + 5]!) {
        // swap-remove
        n--;
        const last = n * 6;
        for (let k = 0; k < 6; k++) p[o + k] = p[last + k]!;
        this.pColor[i] = this.pColor[n]!;
        this.pSize[i] = this.pSize[n]!;
        this.pShape[i] = this.pShape[n]!;
        i--;
        continue;
      }
      p[o] = p[o]! + p[o + 2]! * travel;
      p[o + 1] = p[o + 1]! + p[o + 3]! * travel;
      p[o + 2] = p[o + 2]! * drag;
      p[o + 3] = p[o + 3]! * drag;
      const life = 1 - p[o + 4]! / p[o + 5]!;
      ctx.globalAlpha = life;
      const sz = this.pSize[i]!;
      const shape = this.pShape[i];
      if (shape === SHAPE_SQUARE) {
        ctx.fillStyle = this.pColor[i]!;
        ctx.fillRect(p[o]! - sz / 2, p[o + 1]! - sz / 2, sz, sz);
      } else if (shape === SHAPE_STREAK) {
        // A line back along its motion: it shortens as the spark slows.
        ctx.strokeStyle = this.pColor[i]!;
        ctx.lineWidth = sz * 0.6;
        ctx.beginPath();
        ctx.moveTo(p[o]! - p[o + 2]! * STREAK_FRAMES, p[o + 1]! - p[o + 3]! * STREAK_FRAMES);
        ctx.lineTo(p[o]!, p[o + 1]!);
        ctx.stroke();
      } else {
        // The halo reaches past the core, which is about the size a square spark was.
        const r = sz * 1.4;
        ctx.drawImage(this.spark(this.pColor[i]!) as CanvasImageSource, p[o]! - r, p[o + 1]! - r, r * 2, r * 2);
      }
    }
    ctx.globalAlpha = 1;
    this.pCount = n;
  }
}
